import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNotifications } from '@/contexts/NotificationContext';
import { useSelection } from '@/contexts/SelectionContext';
import i18n from '@/lib/i18n';
import { trackConversion } from '@/lib/analytics';
import { buildKeysetFilter, cursorFromRow, type WorkbenchCursor } from './workbench/keysetCursor';
import { applyBulkDecision, removeBulkDecisions } from './workbench/bulkDecisions';
import type { BulkScope, BulkSummary } from './workbench/bulkRelease';
import { approvalValueFor, ruleContainsFilter, type WorkbenchRuleId, type WorkbenchStatus } from './workbench/statusScope';

/**
 * Re-Export, damit bestehende Testimporte aus `'./useWorkbench'`
 * unveraendert funktionieren (Ruling D13). Die Definition liegt in
 * `statusScope.ts`, damit eine spaetere Stelle (`ruleWorkbenchTarget.ts`)
 * sie ziehen kann, ohne den schweren Hook-Import mitzuschleppen.
 */
export { ruleContainsFilter };
import type { StatusCounts } from './useWorkbenchStatusCounts';
import {
  emptyBuffer,
  appendPage,
  stepForward,
  stepBack,
  needsMore,
  type ReviewBufferState,
} from './workbench/reviewBuffer';
import type { ProductStats } from './useProductStats';

/** Bezugsgroesse eines Quality Scores, so wie `product_scores.score_type` sie fuehrt. */
export type WorkbenchScoreType = 'pre_optimization' | 'post_optimization';

/**
 * Die sechs Komponenten eines Quality Scores, so wie `product_scores` sie
 * fuehrt (`_shared/scoring.ts` ist die Rechenquelle).
 *
 * Seit Task 15 traegt die Werkbank-Karte den Score-Aufbau selbst — vorher lag
 * er auf `/optimization-score/:productId`, einer zweiten Produktdetailflaeche
 * mit eigener Abfrage und eigener Vor-Zurueck-Navigation. Die Zahlen kommen
 * jetzt aus derselben Seitenabfrage wie das Produkt, damit der Aufbau
 * garantiert denselben Score-Eintrag erklaert, den der Kartenkopf anzeigt.
 */
export interface WorkbenchComponentScores {
  title: number;
  description: number;
  highlights: number;
  details: number;
  gpc: number;
  attributes: number;
}

export interface WorkbenchProduct {
  id: string;
  /**
   * Die GMC-Offer-ID (`products.product_id`), nicht der Primaerschluessel.
   *
   * Sie steht im Kartenkopf, weil sie die einzige Kennung ist, die ein Mensch
   * im Merchant Center wiederfindet: die UUID aus `id` kennt Google nicht.
   *
   * Optional aus demselben Grund wie `upload_status` weiter unten: die
   * Test-Fixtures muessen die Spalte nicht setzen. `undefined` heisst "nicht
   * geladen", der Kartenkopf laesst den Platz dann weg statt eine Kennung zu
   * erfinden. (Bis Etappe 4 gab es dafuer einen zweiten Grund: die feed-weite
   * Kandidaten-Abfrage der Sammelfreigabe selektierte nur id + Score. Die ist
   * seitdem serverseitig, siehe `workbench_bulk_summary`.)
   */
  product_id?: string | null;
  title: string | null;
  /**
   * Der Haendler-Titel VOR unserer ersten Optimierung (products.original_title).
   * `null` heisst "kein sauberes Original bekannt" - die Karte zeigt dann kein
   * Vorher, keinen Diff und keinen Zaehler statt eines falschen Vorher.
   * `title` ist dafuer KEIN Ersatz: die Spalte trug bei rund 4.600 Produkten
   * unseren eigenen frueheren Output (09.09.2026).
   */
  original_title: string | null;
  description: string | null;
  product_highlights: string[] | null;
  optimized_title: string | null;
  optimized_description: string | null;
  optimized_product_highlights: string[] | null;
  optimized_product_details: Record<string, any> | null;
  link: string | null;
  image_link: string | null;
  /** Zahlenstring ohne Einheit, etwa "59.9". Die Einheit steht in `currency`. */
  price: string | null;
  /**
   * ISO-4217-Code aus GMC. `null` heisst "vor dem 22.08.2026 zuletzt gesynct",
   * nicht "keine Waehrung". Ohne Waehrung zeigt die Produktkachel keinen Preis,
   * statt eine zu raten.
   */
  currency: string | null;
  brand: string | null;
  gtin: string | null;
  mpn: string | null;
  last_optimized_at: string | null;
  // Original attributes
  color: string | null;
  material: string | null;
  gender: string | null;
  age_group: string | null;
  size: string | null;
  pattern: string | null;
  item_group_id: string | null;
  google_product_category: string | null;
  // Optimized attributes
  optimized_color: string | null;
  optimized_material: string | null;
  optimized_gender: string | null;
  optimized_age_group: string | null;
  optimized_size: string | null;
  optimized_pattern: string | null;
  optimized_google_product_category: string | null;
  // Validation
  validation_warnings: Array<{ field: string; message: string; severity?: string; rule_id?: string }> | null;
  attribute_extraction_status: string | null;
  /**
   * `upload_status` der Zeile. Wird NICHT angezeigt — die eine Aufgabe dieses
   * Feldes ist die optimistische Zaehler-Fortschreibung
   * (`applyDecisionToStats`): eine Zeile mit `'uploaded'` steht weder in
   * `pendingReviewAwaitingUpload` noch nach einer Freigabe in
   * `approvedAwaitingUpload`, beide Anpassungen muessen fuer sie also
   * ausbleiben. Das ist kein Randfall: auf googleshopping.xml tragen 3.830 der
   * 3.961 geladenen `pending_review`-Zeilen `upload_status='uploaded'`
   * (gemessen 2026-07-31), weil die Werkbank ohne Upload-Filter laedt.
   *
   * Optional, weil die Test-Fixtures die Spalte nicht setzen muessen.
   * `undefined` heisst "nicht geladen" und wird wie "nicht hochgeladen"
   * behandelt - dieselbe Annahme wie vor diesem Feld, nur jetzt auf den Fall
   * beschraenkt, in dem wirklich nichts bekannt ist.
   */
  upload_status?: string | null;
  /**
   * Der Merchant hat die Quelldaten geaendert, seit die Optimierung lief. Das
   * Original, gegen das die Karte vergleicht, ist dann nicht mehr das aus dem
   * Shop. `null` heisst "kein Drift erkannt".
   */
  merchant_drift_detected_at?: string | null;
  /** Joined from product_scores (post_optimization preferred, else pre_optimization) */
  totalScore: number | null;
  /**
   * Woher `totalScore` stammt. `pre_optimization` heisst: fuer dieses Produkt
   * gibt es noch keinen Score des optimierten Textes (Score-Job laeuft noch
   * oder ist fehlgeschlagen), die Zahl beschreibt also das Original. Die Karte
   * beschriftet sie dann entsprechend, statt sie als Ergebnis auszugeben.
   */
  scoreType: WorkbenchScoreType | null;
  /**
   * Die sechs Komponenten DESSELBEN Score-Eintrags, aus dem `totalScore`
   * stammt. `null` heisst "keine Score-Zeile vorhanden" (oder die Abfrage hat
   * die Komponenten nicht selektiert) - die Karte benennt das, statt sechs
   * Nullen zu zeigen, die wie ein gemessenes Ergebnis aussehen.
   */
  componentScores: WorkbenchComponentScores | null;
  /**
   * Gesamtwert und Komponenten der `pre_optimization`-Zeile — die Grundlage
   * der Wirkungszeile (Task C1).
   *
   * `null` heisst ausdruecklich "vor dem Lauf wurde nicht gescort", nicht
   * "null Punkte". Genau diese Unterscheidung traegt die Zeile: ohne
   * Vorher-Wert zeigt sie die absoluten Werte und KEIN Delta, denn ein Delta
   * gegen einen fehlenden Wert waere eine erfundene Zahl an der Stelle, an
   * der ueber eine Freigabe entschieden wird.
   *
   * Bewusst getrennt von `componentScores` gehalten: das bleibt der Aufbau zu
   * `totalScore` und damit zu DERSELBEN Zeile. Beides in ein Feld zu mischen
   * waere genau der Fehler, den der Kommentar dort ausschliesst.
   */
  preTotalScore: number | null;
  preComponentScores: WorkbenchComponentScores | null;
  /**
   * Score v3: fuehrt die Kategorie dieses Produkts ueberhaupt Attribute?
   *
   * Aus `product_scores.attribute_applicable` DERSELBEN Zeile, aus der
   * `totalScore` stammt. `false` heisst, die Edge-Seite hat den Attribut-Bucket
   * weggelassen und die uebrigen fuenf renormiert; `attribute_score` ist dann
   * eine 0 ohne Aussage, und die Karte darf sie weder als Balken zeigen noch
   * als schwaechsten Bereich nennen.
   *
   * `true`, wenn keine Score-Zeile vorliegt oder die Spalte nicht selektiert
   * wurde -- dieselbe Vorgabe wie in der Datenbank (`NOT NULL DEFAULT true`).
   * "Wir wissen es nicht" heisst hier "wie bisher", nicht "gibt es nicht": die
   * umgekehrte Richtung verschwiege den Bereich auf jeder Karte mit
   * unvollstaendig geladenem Score.
   */
  attributeApplicable: boolean;
  /**
   * Score v3.1: wann die beiden Score-Zeilen gerechnet wurden
   * (`product_scores.calculated_at`), aus DENSELBEN Zeilen wie
   * `preComponentScores` und `componentScores`.
   *
   * Einziger Zweck ist der Epochen-Guard der GPC-Komponente
   * (`scoreTouch.ts:gpcEpochComparable`): der GPC-Wert war bis v3.0 binaer
   * (100 fuer jede gesetzte Kategorie) und zaehlt seit v3.1 die Tiefe. Ohne
   * die Zeitstempel meldete die Karte fuer jedes Bestandsprodukt "Nach der
   * Optimierung niedriger als vorher" -- ueber einen reinen Formelwechsel.
   *
   * `null` heisst "nicht bekannt" (keine Zeile, oder die Abfrage hat die
   * Spalte nicht selektiert), und der Guard liest das als "nicht
   * vergleichbar". Keine Aussage ist besser als eine falsche.
   */
  preCalculatedAt: string | null;
  postCalculatedAt: string | null;
}

/**
 * Ergebnis eines Sammel-Rueckwegs.
 *
 * Zwei Zahlen statt einer, weil ein Teilausfall sonst nicht darstellbar ist:
 * schlaegt der zweite von drei Bloecken fehl, traegt `zurueck` eine echte
 * Teilsumme, und ein Aufrufer, der nur `> 0` prueft, meldet Erfolg, waehrend
 * der Rest freigegeben bleibt. `vollstaendig` trennt "alles zurueck" von
 * "teilweise zurueck, bitte noch einmal".
 */
export interface BulkReopenResult {
  /** Wieviele Zeilen die Datenbank tatsaechlich zurueckgesetzt hat. */
  zurueck: number;
  /** false = mindestens ein Block ist gescheitert, ein zweiter Versuch lohnt. */
  vollstaendig: boolean;
}

export interface WorkbenchEditState {
  optimized_title: string;
  optimized_description: string;
  optimized_product_highlights: string[];
  optimized_color: string;
  optimized_material: string;
  optimized_gender: string;
  optimized_age_group: string;
  optimized_size: string;
  optimized_pattern: string;
  optimized_google_product_category: string;
}

/** approval_status written to DB */
export type ReviewDecision = 'pending_review' | 'approved' | 'rejected';
export type ScoreFilter = 'all' | 'critical' | 'warning' | 'good';
export type SortOrder = 'asc' | 'desc';

export interface WorkbenchFilters {
  search: string;
  scoreFilter: ScoreFilter;
  sortOrder: SortOrder;
  /**
   * Vierte Filterdimension: nur Produkte mit mindestens einer Beanstandung in
   * `products.validation_warnings`.
   *
   * Bewusst NEBEN `scoreFilter` und nicht als fuenfter Wert darin: die vier
   * Punkte-Pillen sind ein Entweder-oder ueber ein Band der Gesamtpunktzahl,
   * eine Beanstandung ist davon unabhaengig. Beide duerfen gleichzeitig
   * gesetzt sein ("kritisch UND beanstandet").
   *
   * Der Unterschied ist nicht theoretisch: `scoreFilter: 'warning'` meint das
   * Punkte-Band 40 bis 69, `warningsOnly` meint die Regelverstoesse aus der
   * Validierung. Ein Titel mit 82 Punkten kann eine unbelegte Norm-Behauptung
   * tragen, ein Produkt mit 51 Punkten voellig beanstandungsfrei sein. Genau
   * diese Verwechslung war der Defekt, den dieses Feld behebt: der
   * Menueeintrag "N mit Hinweisen zuerst ansehen" zaehlte Beanstandungen und
   * filterte danach ein Punkte-Band.
   */
  warningsOnly: boolean;
  /**
   * Fuenfte Filterdimension (JourneyBar/Statusfluss, Spec 5.2): nur Produkte,
   * die noch NICHT bei Google liegen (`upload_status IS NULL` oder
   * `<> 'uploaded'`).
   *
   * Anders als `warningsOnly` und der Punkte-Filter laeuft dieser Filter
   * SERVERSEITIG in `fetchProductPage` und in der Gesamtzahl-Abfrage — nicht
   * clientseitig auf den bereits geladenen Zeilen. Grund ist "Zahl = Liste"
   * (Spec 4): die Journey-Chips zeigen die Schnittmenge "wartet UND nicht bei
   * Google" (`pendingReviewAwaitingUpload`/`approvedAwaitingUpload`, siehe
   * `useProductStats.ts`), und ein clientseitiger Filter auf dem geladenen
   * Ausschnitt haette diese Zahl nicht zuverlaessig nachbilden koennen — die
   * Werkbank laedt sonst auch laengst hochgeladene Zeilen (auf
   * googleshopping.xml 3.830 von 3.961 offenen, gemessen 2026-07-31).
   *
   * Gesetzt wird er ausschliesslich ueber den URL-Parameter
   * `scope=awaiting_upload` (`hasAwaitingUploadScope`) — anders als der
   * Reiter ist das KEINE Praeferenz, kein localStorage: der Filter gilt nur,
   * solange der Parameter in der URL steht.
   */
  awaitingUploadOnly: boolean;

  /**
   * Sechste Filterdimension (UX-Audit WP5, A2O-02): nur Produkte, deren
   * `validation_warnings` die genannte Regel tragen. Serverseitig wie
   * `awaitingUploadOnly` (Zahl = Liste: der Feed-Check nennt "12 Titel ueber
   * 150 Zeichen", der Klick muss genau diese zeigen), gesetzt nur ueber den
   * URL-Parameter `rule`. Optional, damit bestehende Filter-Literale gueltig
   * bleiben; fehlt es, gilt null.
   */
  ruleId?: WorkbenchRuleId | null;
}

export const DEFAULT_FILTERS: WorkbenchFilters = {
  search: '',
  scoreFilter: 'all',
  sortOrder: 'desc',
  warningsOnly: false,
  awaitingUploadOnly: false,
  ruleId: null,
};

/**
 * Das Praedikat fuer "noch nicht bei Google", WORTGLEICH aus
 * `useProductStats.ts` uebernommen (dort zweimal, fuer `approved` und
 * `pending_review`). `.or('upload_status.is.null,upload_status.neq.uploaded')`
 * statt `.neq('upload_status','uploaded')`, weil `<>` NULL-Zeilen ausschliesst
 * — ein Produkt ohne je gesetzten Upload-Status ist nicht hochgeladen und
 * gehoert genau deshalb in die Menge.
 *
 * Zwei Kopien derselben Zeichenkette statt eines gemeinsamen Imports, weil
 * `useProductStats.ts` sie woertlich stehen lassen MUSS (Spec 5.2): der
 * Textwaechter `src/test/awaiting-upload-predicate.test.ts` liest beide
 * Dateien per `fs` und pinnt die Gleichheit — eine Drift zwischen Chip-Zahl
 * und Werkbank-Liste faellt damit im Test auf, nicht erst beim Nutzer.
 */
export const AWAITING_UPLOAD_OR = 'upload_status.is.null,upload_status.neq.uploaded';

// Die sechs Komponenten-Spalten haengen seit Task 15 mit im Join: der
// Score-Aufbau ist ein Bereich der Karte, keine zweite Seite mit zweiter
// Abfrage mehr. Sechs `smallint`-Spalten auf einer ohnehin geholten Zeile,
// kein zusaetzlicher Round-Trip.
// `upload_status` wird nirgends angezeigt und ist trotzdem hier: ohne die
// Spalte kann die optimistische Zaehler-Fortschreibung nicht wissen, ob die
// entschiedene Zeile bereits bei Google liegt — siehe das Feld in
// `WorkbenchProduct` und `applyDecisionToStats`.
export const WORKBENCH_COLUMNS = 'id, product_id, title, original_title, description, product_highlights, optimized_title, optimized_description, optimized_product_highlights, optimized_product_details, link, image_link, price, currency, brand, gtin, mpn, last_optimized_at, color, material, gender, age_group, size, pattern, item_group_id, google_product_category, optimized_color, optimized_material, optimized_gender, optimized_age_group, optimized_size, optimized_pattern, optimized_google_product_category, validation_warnings, attribute_extraction_status, upload_status, merchant_drift_detected_at, product_scores(product_id, total_score, score_type, title_score, description_score, highlights_score, product_details_score, gpc_score, attribute_score, attribute_applicable, calculated_at)';

/** Seitengroesse des Reviewer-Puffers. Ersetzt das alte Offset-Fenster. */
export const REVIEW_PAGE_SIZE = 100;
/** Ab wieviel Restvorlauf die naechste Seite geholt wird. */
const REVIEW_PREFETCH = 20;

/**
 * P0-4 Nachbesserung F2: ab wieviel Restvorlauf im score-gefilterten Kartenmodus
 * die naechste Rohseite nachgeladen wird. Gleicher Schwellenwert wie
 * PREFETCH_ROWS_FROM_END in WorkbenchListView.tsx (dort fuer den Listenmodus),
 * hier separat gehalten, weil beide Stellen unabhaengig voneinander lesbar
 * bleiben sollen (keine Kopplung ueber Komponentengrenzen).
 */
const CARD_SCORE_FILTER_PREFETCH = 10;

/**
 * P0-4 Review-Runde 2, Fund N1: Deckel fuers automatische Nachladen, in
 * Zeilen (10 Seiten * REVIEW_PAGE_SIZE). Ohne Deckel scannt ein score-
 * gefilterter Pfad, der auf einem gut optimierten Feed keine Treffer mehr
 * findet, den GANZEN Feed durch — auf 69k Produkten ~690 sequentielle
 * 100er-Requests, nur mit einem Skeleton auf dem Schirm (10x mehr Requests
 * als der Vor-Task-Zustand mit 1000er-Seiten). Automatisches Nachladen
 * (sowohl der F2-Karten-Effect hier als auch der Nachlade-Effect in
 * WorkbenchListView.tsx) stoppt bei diesem Deckel; ist er erreicht und
 * `hasNextPage` noch true, zeigt die UI einen ehrlichen Zustand mit
 * Handlungsangebot statt endlos weiterzuscannen — `unlockMoreAutoLoad`
 * gibt genau ein weiteres Batches dieser Groesse frei.
 */
export const AUTO_LOAD_ROW_CAP = 1000;

/**
 * K5-Befund: hoechster per Weiter/Pfeiltaste erreichbarer Index. Ohne Deckel
 * ist das total-1 (Bewegung ueber das Geladene hinaus stoesst das Nachladen
 * an); AM Deckel endet die Navigation an der letzten geladenen Zeile — sonst
 * wandert der Zeiger unsichtbar weiter, waehrend die Anzeige klemmt. Pure
 * Funktion, damit die Klemme testbar ist.
 */
export function maxNavigableIndex(total: number, loadedLength: number, capReached: boolean): number {
  return (capReached ? Math.min(total, loadedLength) : total) - 1;
}

/**
 * Reine Kernrechnung des N1-Deckels, exportiert damit sie ohne React/Query
 * getestet werden kann (Review-Runde 2, "Deckel-Logik als reine Funktion
 * testbar machen"). `unlockedBatches` startet bei 0 (Grunddeckel = `cap`
 * Zeilen); jeder Klick auf "Weiter suchen" (`unlockMoreAutoLoad`) erhoeht ihn
 * um 1 und gibt ein weiteres Batch dieser Groesse frei.
 */
export function computeAutoLoadRowLimit(unlockedBatches: number, cap: number = AUTO_LOAD_ROW_CAP): number {
  return cap * (unlockedBatches + 1);
}

/** Ist der aktuelle Deckel erreicht oder ueberschritten? Reine Funktion, siehe computeAutoLoadRowLimit. */
export function isAutoLoadCapReached(
  loadedCount: number,
  unlockedBatches: number,
  cap: number = AUTO_LOAD_ROW_CAP,
): boolean {
  return loadedCount >= computeAutoLoadRowLimit(unlockedBatches, cap);
}

// isAutoLoadCtaVisible lebt in ./workbench/emptyStateGates.ts, NICHT hier —
// `WorkbenchReviewer.test.tsx` mockt `@/hooks/useWorkbench` komplett (siehe
// Review-Runde 2, F3-Doku dort), ein Export hier waere in dem Mock nicht
// nutzbar. Gleiches Muster wie isAllReviewedEmptyState/isNoProductsEmptyState.

interface RawScoreRow {
  product_id: string;
  total_score: number;
  score_type: string;
  // Optional, weil nicht jede Abfrage diese Spalten selektiert. `undefined`
  // heisst also "nicht geladen", `null` heisst "geladen, aber in der Datenbank
  // leer" - zwei verschiedene Aussagen.
  title_score?: number | null;
  description_score?: number | null;
  highlights_score?: number | null;
  product_details_score?: number | null;
  gpc_score?: number | null;
  attribute_score?: number | null;
  /**
   * Score v3. `undefined` heisst "nicht selektiert" (Fixtures, aeltere
   * Abfragen), `null` kann eine Zeile von vor der Migration tragen. Beides
   * wird zu `true` gelesen, siehe `WorkbenchProduct.attributeApplicable`.
   */
  attribute_applicable?: boolean | null;
  /**
   * Score v3.1, fuer den Epochen-Guard der GPC-Komponente. `undefined` heisst
   * wie oben "nicht selektiert"; der Guard behandelt das wie einen fehlenden
   * Wert und vergleicht dann gar nicht.
   */
  calculated_at?: string | null;
}

interface PickedScore {
  totalScore: number | null;
  scoreType: WorkbenchScoreType | null;
  componentScores: WorkbenchComponentScores | null;
  preTotalScore: number | null;
  preComponentScores: WorkbenchComponentScores | null;
  attributeApplicable: boolean;
  preCalculatedAt: string | null;
  postCalculatedAt: string | null;
}

/**
 * Baut die sechs Komponenten aus einer Score-Zeile.
 *
 * Gibt `null` zurueck, wenn die Komponenten gar nicht selektiert wurden — dann
 * gibt es nichts zu zeigen, und sechs Nullen waeren eine Behauptung ueber
 * Daten, die diese Abfrage nie geholt hat. Ein einzelnes `null` IN der Zeile
 * ist dagegen eine echte 0 (so rechnet auch der Feed-Aufklapper), betrifft
 * real nur `product_details_score`, `gpc_score` und `attribute_score`.
 */
function componentScoresFrom(row: RawScoreRow): WorkbenchComponentScores | null {
  const raw = [
    row.title_score,
    row.description_score,
    row.highlights_score,
    row.product_details_score,
    row.gpc_score,
    row.attribute_score,
  ];
  if (raw.every((value) => value === undefined)) return null;

  return {
    title: Number(row.title_score ?? 0),
    description: Number(row.description_score ?? 0),
    highlights: Number(row.highlights_score ?? 0),
    details: Number(row.product_details_score ?? 0),
    gpc: Number(row.gpc_score ?? 0),
    attributes: Number(row.attribute_score ?? 0),
  };
}

/**
 * Pick post_optimization score over pre_optimization; return null if no scores.
 *
 * Gibt seit F3 auch zurueck, WOHER die Zahl stammt. Ohne Post-Score faellt die
 * Auswahl auf die erste Zeile zurueck, und das ist dann der Score des
 * UNOPTIMIERTEN Produkts. Unter der Beschriftung "N von 100" am Kartenkopf
 * behauptet er etwas ueber den optimierten Text, den er nie gesehen hat. Die
 * Karte muss die Bezugsgroesse deshalb benennen koennen.
 */
function pickBestScore(scores: RawScoreRow[] | null): PickedScore {
  if (!scores || scores.length === 0) {
    return {
      totalScore: null,
      scoreType: null,
      componentScores: null,
      preTotalScore: null,
      preComponentScores: null,
      // Ohne Score-Zeile gibt es kein Urteil ueber Anwendbarkeit. Die Karte
      // zeigt dann ohnehin keine Balken (`componentScores` ist null).
      attributeApplicable: true,
      preCalculatedAt: null,
      postCalculatedAt: null,
    };
  }
  const post = scores.find((s) => s.score_type === 'post_optimization');
  const row = post ?? scores[0];

  /**
   * Task C1: die `pre_optimization`-Zeile wird nicht mehr weggeworfen.
   *
   * Die Abfrage holt ohnehin BEIDE Zeilen (der verschachtelte Select hat kein
   * Praedikat auf `score_type`) — bis hierher landete die zweite im Nichts.
   * Es braucht also keine Schemaaenderung und keine zweite Abfrage, nur das
   * Aufheben dessen, was schon da ist.
   *
   * WICHTIG: nur eine ECHTE pre-Zeile zaehlt. Fehlt der Post-Score, faellt
   * `row` oben auf die erste Zeile zurueck — das ist dann die pre-Zeile
   * selbst, und sie als "vorher" UND "nachher" zu fuehren ergaebe ein Delta
   * von 0 gegen sich selbst. Die Wirkungszeile zeigte dann "±0 geprueft",
   * wo in Wahrheit gar kein Nachher gemessen wurde.
   */
  const preRow = post ? scores.find((s) => s.score_type === 'pre_optimization') : undefined;

  return {
    totalScore: Number(row.total_score),
    scoreType: row.score_type === 'post_optimization' ? 'post_optimization' : 'pre_optimization',
    // Bewusst aus DERSELBEN Zeile: ein Aufbau, der eine andere Score-Zeile
    // erklaert als die Zahl am Kartenkopf, waere schlimmer als kein Aufbau.
    componentScores: componentScoresFrom(row),
    preTotalScore: preRow ? Number(preRow.total_score) : null,
    preComponentScores: preRow ? componentScoresFrom(preRow) : null,
    /**
     * DIE ZEILE, DIE DIE ZAHLEN LIEFERT, LIEFERT AUCH DIE ANWENDBARKEIT.
     *
     * Immer `row`, also dieselbe Zeile wie `componentScores` -- auch dann, wenn
     * `row` mangels Post-Score die pre-Zeile IST. Die Anwendbarkeit beschreibt
     * den Bucket-Zuschnitt, mit dem genau diese sechs Zahlen gerechnet wurden;
     * ein Urteil aus einer anderen Zeile daneben zu stellen waere eine Aussage
     * ueber eine Rechnung, die die Karte gar nicht zeigt.
     *
     * ABWEICHUNG VOM BRIEF, bewusst und vom Controller so entschieden (Task A5,
     * Fix-Runde 1): der Brief verlangte fuer den Fall "nur eine pre-Zeile" die
     * Vorgabe `true`. Das waere inkonsistent -- die Karte zeigt dann die
     * Werte DER PRE-ZEILE (mit `score.preNote` als Hinweis), und ein hart auf
     * `true` gesetzter Attribut-Balken neben einem pre-Score, der ohne
     * Attribut-Bucket gerechnet wurde, zeigte eine 0, die es nie gab. Steht in
     * der pre-Zeile `false`, gilt `false`.
     *
     * `?? true` deckt nur die beiden echten Nichtwisser-Faelle ab: Spalte nicht
     * selektiert (`undefined`) und Zeile von vor der Migration (`null`).
     * Gepinnt von den drei Tests "Anwendbarkeits-Quelle" in
     * `useWorkbench.test.ts`.
     */
    attributeApplicable: row.attribute_applicable ?? true,
    /**
     * Score v3.1: die Rechenzeitpunkte DERSELBEN zwei Zeilen, aus denen die
     * Komponenten oben stammen -- `postCalculatedAt` also aus `row`, auch
     * dann, wenn `row` mangels Post-Score die pre-Zeile IST. Dann ist
     * `preCalculatedAt` null, und der Epochen-Guard vergleicht ohnehin nicht.
     */
    postCalculatedAt: row.calculated_at ?? null,
    preCalculatedAt: preRow?.calculated_at ?? null,
  };
}

/**
 * Laedt EINE Seite des aktiven Statusreiters ab dem Cursor.
 *
 * Bewusst kein `.range()`: die Menge schrumpft durch die eigene Freigabe des
 * Nutzers, ein Offset wandert dabei mit und ueberspringt Produkte (P0-1).
 * Die Sortierung muss deterministisch und total sein, sonst kann der Cursor
 * bei gleichem Zeitstempel Zeilen auslassen.
 *
 * OFFENE FOLGEARBEIT (Nachfasser Welle 9): gefiltert wird nur auf den
 * Reiterwert von `approval_status`, NICHT auf den Upload-Zustand. Die Liste
 * enthaelt damit auch Produkte, deren Fassung laengst bei Google ausgeliefert
 * wird — auf googleshopping.xml 3.830 von 3.961 Zeilen (gemessen 2026-07-31),
 * weil `approval_status` nach einem Upload nirgends zurueckgesetzt wird. Wer
 * hier durchklickt, entscheidet ueberwiegend ueber bereits Ausgeliefertes,
 * ohne dass die Oberflaeche das sagt. Zu tun: die Liste auf die
 * noch-nicht-live-Menge scopen oder wenigstens filterbar machen. Bis dahin
 * gilt die Zwischenloesung, dass alle ZAHLEN die Schnittmenge nennen
 * (`pendingReviewAwaitingUpload`, siehe `applyDecisionToStats` und
 * `lib/journey.ts`) und nur die LISTENLAENGE den rohen Vorrat.
 */
export async function fetchProductPage(
  feedId: string,
  search: string,
  cursor: WorkbenchCursor | null,
  limit: number = REVIEW_PAGE_SIZE,
  /**
   * Der Statusreiter. Steht bewusst als LETZTER Parameter mit Standardwert:
   * jeder bestehende Aufrufer meint "offen", und ein Standard an dieser
   * Stelle haelt sie alle gueltig, statt sie stillschweigend umzudeuten.
   */
  status: WorkbenchStatus = 'pending',
  /**
   * Der Filter "noch nicht bei Google" (Spec 5.2). Diese Funktion ist die
   * "Puffer-Abfrage" — der Aufruf der Infinite-Query im Hook — und muss
   * deshalb dasselbe Praedikat kennen wie die Gesamtzahl-Abfrage
   * (`workbench-server-total`), sonst zeigte die Liste mehr Zeilen, als der
   * Kopf zaehlt. Letzter Parameter mit Standardwert aus demselben Grund wie
   * `status`: jeder bestehende Aufrufer meint "kein Filter".
   */
  awaitingUploadOnly: boolean = false,
  /** WP5 A2O-02: die Regel aus dem URL-Parameter `rule`, oder null. */
  ruleId: WorkbenchRuleId | null = null,
): Promise<WorkbenchProduct[]> {
  let query = supabase
    .from('products')
    .select(WORKBENCH_COLUMNS)
    .eq('feed_id', feedId)
    .eq('optimization_status', 'completed')
    .eq('approval_status', approvalValueFor(status))
    .order('last_optimized_at', { ascending: true, nullsFirst: false })
    .order('id', { ascending: true });

  if (awaitingUploadOnly) {
    query = query.or(AWAITING_UPLOAD_OR);
  }

  if (ruleId) {
    // Array-Objekt NIE roh an `.contains()`: postgrest-js serialisiert ein
    // Array-Argument als Postgres-Array (`cs.{...}`, `Array.join(',')`), das
    // fuer ein Array von Objekten `cs.{[object Object]}` ergibt, also kein
    // gueltiges jsonb; PostgREST antwortet mit 400 oder 0 Treffern. Als
    // String uebergeben nimmt postgrest-js den Wert woertlich (`cs.${value}`)
    // und PostgREST castet das jsonb-`@>`.
    query = query.contains('validation_warnings', JSON.stringify(ruleContainsFilter(ruleId)));
  }

  const keyset = buildKeysetFilter(cursor);
  if (keyset) query = query.or(keyset);

  if (search.trim()) {
    const term = `%${search.trim()}%`;
    query = query.or(`title.ilike.${term},brand.ilike.${term},optimized_title.ilike.${term}`);
  }

  const { data, error } = await query.limit(limit);
  if (error) throw error;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase nested select types are loosely typed
  return (data ?? []).map((row: any) => {
    const { product_scores, ...product } = row;
    return {
      ...product,
      ...pickBestScore(product_scores as RawScoreRow[] | null),
    } as WorkbenchProduct;
  });
}

/**
 * `getNextPageParam` fuer die `useInfiniteQuery` in `useWorkbench` — als
 * eigenstaendige, reine Funktion exportiert, damit sie ohne React/Query
 * getestet werden kann (Review-Fund F7). Eine volle Seite (== REVIEW_PAGE_SIZE
 * Zeilen) heisst "es koennte noch mehr geben" -> Cursor der letzten Zeile;
 * eine kuerzere Seite ist per Definition die letzte -> `undefined` (React
 * Query interpretiert das als "keine weitere Seite").
 *
 * F9: nutzt `cursorFromRow` aus `keysetCursor.ts` statt einer zweiten, inline
 * duplizierten `{ lastOptimizedAt, id }`-Konstruktion — eine Quelle fuer die
 * Cursor-Ableitung, dieselbe wie beim initialen Page-Fetch.
 */
export function getNextPageParamForProducts(
  lastPage: WorkbenchProduct[],
  pageSize: number = REVIEW_PAGE_SIZE,
): WorkbenchCursor | undefined {
  if (lastPage.length < pageSize) return undefined;
  return cursorFromRow(lastPage[lastPage.length - 1]);
}

/**
 * Zeilen je Schreib-Aufruf der Sammelfreigabe (Etappe 4).
 *
 * Gemessen kostet das Schreiben 0,086 ms je Zeile, das statement_timeout der
 * Rolle `authenticated` ist 8 s. 10.000 Zeilen sind damit rund 0,9 s reine
 * Schreibzeit - genug Luft fuer den Lesedurchgang, der im selben Statement
 * steckt. Die RPC klemmt den Wert serverseitig ohnehin auf 1..20.000.
 */
const BULK_WRITE_CHUNK = 10_000;

/**
 * Notbremse fuer die Schreibschleife von `approveScope`.
 *
 * 25 * 10.000 = 250.000 Zeilen, weit ueber jedem realen Feed (groesster im
 * Bestand: 61.022 offene). Der Deckel ist kein erwarteter Abbruchgrund,
 * sondern der Schutz gegen eine Endlosschleife, falls die RPC jemals eine
 * Menge meldet, die sie nicht schrumpfen laesst.
 */
const BULK_APPROVE_MAX_ROUNDS = 25;

/**
 * Rohzeile von `workbench_bulk_summary` (Etappe 4).
 *
 * Die fuenf Zaehler sind in Postgres `bigint`. PostgREST liefert bigint als
 * JSON-Zahl, solange sie in einen `double` passt - hier immer, es sind
 * Produktzahlen. Trotzdem laeuft jedes Feld durch `Number()`, weil ein
 * kuenftiger Wechsel auf String-Serialisierung sonst still `"23401" > 0`
 * durchgehen liesse und die Zahlen als Text in die Rueckfrage schriebe.
 *
 * Eigener Typ statt `BulkSummary`, weil das die snake_case-Aussenseite ist:
 * `BulkSummary` (camelCase, `bulkRelease.ts`) ist der Vertrag nach oben.
 */
interface BulkSummaryRow {
  open_count: number | string | null;
  safe_count: number | string | null;
  warning_count: number | string | null;
  low_score_count: number | string | null;
  unscored_count: number | string | null;
}

/**
 * Rohzeile von `workbench_bulk_approve` (Etappe 4). Die RPC liefert IMMER
 * genau eine Zeile, auch im Ablehnungsfall (dann mit 0 und leerem Array) -
 * eine fehlende Zeile waere also ein Vertragsbruch und wird als "nichts
 * geschrieben" behandelt, nicht als Fehler.
 */
interface BulkApproveRow {
  approved_count: number | string | null;
  approved_ids: string[] | null;
}

function applyScoreFilterAndSort(
  products: WorkbenchProduct[],
  filters: WorkbenchFilters,
): WorkbenchProduct[] {
  let filtered = products;

  if (filters.scoreFilter === 'critical') {
    filtered = filtered.filter((p) => p.totalScore !== null && p.totalScore < 40);
  } else if (filters.scoreFilter === 'warning') {
    filtered = filtered.filter((p) => p.totalScore !== null && p.totalScore >= 40 && p.totalScore < 70);
  } else if (filters.scoreFilter === 'good') {
    filtered = filtered.filter((p) => p.totalScore !== null && p.totalScore >= 70);
  }

  // Beanstandungen (Spec 9.1). Steht NACH dem Punkte-Band und nicht in dessen
  // if/else-Kette, weil beide Dimensionen unabhaengig sind und sich
  // ueberlagern duerfen: "kritisch UND beanstandet" ist eine sinnvolle Menge.
  //
  // EHRLICHKEIT ZUR REICHWEITE, die niemand wegdiskutieren darf: dieser
  // Filter arbeitet wie die Punkte-Pillen ausschliesslich auf den BEREITS
  // GELADENEN Zeilen. `fetchProductPage` kennt ihn nicht, er erreicht den
  // Server nie (Begruendung am Query-Key, Review-Fund F4). Die Zahl am
  // Menueeintrag "N mit Hinweisen zuerst ansehen" kommt dagegen aus der RPC
  // `workbench_bulk_summary` und ist FEED-WEIT. Beide Zahlen beschreiben
  // damit verschiedene Mengen: das Menue zaehlt den ganzen Feed, die
  // gefilterte Liste zeigt den Puffer. Auf einem grossen Feed sieht der
  // Nutzer nach dem Klick also zunaechst weniger Treffer als versprochen und
  // muss weiter nachladen, bis jede beanstandete Zeile geladen ist. Eine
  // serverseitige Vorauswahl waere ein eigener Umbau (Praedikat in
  // fetchProductPage, eigener Query-Key, Neuladen bei jedem Umschalten) und
  // ist hier bewusst NICHT gemacht.
  //
  // `Array.isArray` statt eines blossen Wahrheitstests: die Spalte ist
  // nullable (`validation_warnings: Array<...> | null`) und traegt auf jeder
  // noch nicht validierten Zeile NULL. Ein `p.validation_warnings.length`
  // waere ein Absturz mitten im Renderpfad, und ein leeres Array ist
  // ausdruecklich "geprueft, nichts gefunden" und damit kein Treffer.
  if (filters.warningsOnly) {
    filtered = filtered.filter(
      (p) => Array.isArray(p.validation_warnings) && p.validation_warnings.length > 0,
    );
  }

  return filtered.sort((a, b) => {
    const scoreA = a.totalScore ?? -1;
    const scoreB = b.totalScore ?? -1;
    return filters.sortOrder === 'asc' ? scoreA - scoreB : scoreB - scoreA;
  });
}

/**
 * Schreibt eine einzelne Accept/Reject-Entscheidung optimistisch in die
 * Feed-Statistik fort, ohne die elf COUNT-Abfragen aus useProductStats
 * neu anzustossen. Reine Funktion, damit sie ohne React testbar ist (N1).
 *
 * Vier Anzeigen haengen an dem, was hier passiert, und alle vier werden
 * gemeinsam optimistisch genaehrt: der Werkbank-Kopf (`pendingReview`), die
 * Verlustzeile des Feed-Check-Kopfs und die Wartezahl der Freigabe-Stufe in
 * den JourneyChips (beide `pendingReviewAwaitingUpload`, seit dem Nachfasser zu
 * Welle 9 dieselbe Zahl) sowie die Handlungskarte des Dashboards
 * (`approvedAwaitingUpload` im Upload-Zweig). Ohne die Fortschreibung zieht
 * der Zeiger erst beim naechsten Refetch nach: zwei Anzeigen desselben
 * Vorgangs, die sich sekundenlang widersprechen.
 *
 * `alreadyUploaded` loest genau diesen Anspruch fuer die haeufigste Zeile der
 * Werkbank ein. Beide `…AwaitingUpload`-Zahlen sind Schnittmengen mit
 * `upload_status <> 'uploaded'`; eine bereits live liegende Zeile steht in
 * keiner von beiden. Ohne das Flag senkte jede Entscheidung ueber eine solche
 * Zeile `pendingReviewAwaitingUpload` (und hob bei einer Freigabe
 * `approvedAwaitingUpload`), obwohl sich an beiden Mengen nichts aendert — auf
 * googleshopping.xml betrifft das 3.830 der 3.961 geladenen Zeilen. Sichtbare
 * Folge: die Karte bot "N Produkte zu Google uebertragen" fuer Produkte an,
 * die dort schon liegen, und die Verlustzeile zaehlte gleichzeitig eine echte
 * Wartemenge herunter, die die Entscheidung nie beruehrt hat.
 *
 * `pendingReview` (roher Vorrat) und `approved`/`rejected` (Lebenszeit-
 * Bestaende) aendern sich dagegen IMMER: die Zeile stand auf `pending_review`
 * und verlaesst diesen Zustand, unabhaengig davon, wo sie bei Google steht.
 */
export function applyDecisionToStats(
  stats: ProductStats,
  decision: 'approved' | 'rejected',
  /** `upload_status === 'uploaded'` der entschiedenen Zeile. Default false =
   *  "nicht bei Google" (auch fuer "unbekannt", siehe `WorkbenchProduct`). */
  alreadyUploaded = false,
): ProductStats {
  return {
    ...stats,
    pendingReview: Math.max(0, stats.pendingReview - 1),
    // Der Math.max bleibt als Boden fuer den Fall "Upload-Zustand unbekannt"
    // (Zeile nicht mehr geladen): dann traegt der naechste Refetch den
    // gemessenen Wert nach, aber die Anzeige rutscht zwischenzeitlich nicht
    // ins Negative.
    pendingReviewAwaitingUpload: alreadyUploaded
      ? stats.pendingReviewAwaitingUpload
      : Math.max(0, stats.pendingReviewAwaitingUpload - 1),
    approved: decision === 'approved' ? stats.approved + 1 : stats.approved,
    approvedAwaitingUpload:
      decision === 'approved' && !alreadyUploaded
        ? stats.approvedAwaitingUpload + 1
        : stats.approvedAwaitingUpload,
    rejected: decision === 'rejected' ? stats.rejected + 1 : stats.rejected,
  };
}

/**
 * Kehrseite von applyDecisionToStats fuer den Undo aus dem Ablehnen-Toast:
 * nimmt genau EINE Ablehnung wieder aus der Statistik heraus (das Produkt
 * steht danach wieder auf pending_review). Reine Funktion, damit sie ohne
 * React testbar ist — Muster applyDecisionToStats.
 */
/**
 * K6-Befund: der Fehler-Rollback der Einzel-Entscheidungen war reine
 * Invalidierung und hing damit an genau dem Kanal, der gerade versagt hatte —
 * scheiterte im selben Stoerungsfenster auch der Rollback-Refetch (~3 s
 * Retry-Budget), froren die optimistisch verschobenen Zaehler ein, und jeder
 * weitere Fehlversuch verschob um 1 mehr. Diese LOKALE Kehrseite rechnet die
 * Verschiebung zurueck, ohne das Netz zu brauchen; die Invalidierung danach
 * bleibt als Abgleich. Reine Funktion, Muster applyDecisionToStats.
 */
export function revertDecisionFromStats(
  stats: ProductStats,
  decision: 'approved' | 'rejected',
  alreadyUploaded = false,
): ProductStats {
  return {
    ...stats,
    pendingReview: stats.pendingReview + 1,
    pendingReviewAwaitingUpload: alreadyUploaded
      ? stats.pendingReviewAwaitingUpload
      : stats.pendingReviewAwaitingUpload + 1,
    approved: decision === 'approved' ? Math.max(0, stats.approved - 1) : stats.approved,
    approvedAwaitingUpload:
      decision === 'approved' && !alreadyUploaded
        ? Math.max(0, stats.approvedAwaitingUpload - 1)
        : stats.approvedAwaitingUpload,
    rejected: decision === 'rejected' ? Math.max(0, stats.rejected - 1) : stats.rejected,
  };
}

export function revertRejectFromStats(
  stats: ProductStats,
  /** Muss dasselbe sein wie beim zugehoerigen `applyDecisionToStats`-Aufruf,
   *  sonst ist die Kehrseite keine: bei einer live liegenden Zeile hat die
   *  Ablehnung die Schnittmenge nicht gesenkt, ein Inkrement hier erfaende
   *  also eine Wartemenge, die es nie gab. */
  alreadyUploaded = false,
): ProductStats {
  return {
    ...stats,
    pendingReview: stats.pendingReview + 1,
    // Kehrseite des Dekrements in applyDecisionToStats: das Produkt steht
    // wieder auf pending_review. Nur wenn es nicht schon bei Google liegt,
    // wandert es damit zurueck in die Wartemenge.
    pendingReviewAwaitingUpload: alreadyUploaded
      ? stats.pendingReviewAwaitingUpload
      : stats.pendingReviewAwaitingUpload + 1,
    rejected: Math.max(0, stats.rejected - 1),
  };
}

/**
 * Verschiebt Produkte zwischen zwei Reiterzahlen.
 *
 * V2-Befund: der Schluessel `workbench-status-counts` wurde im ganzen Repo an
 * genau EINER Stelle invalidiert (`reopenProduct`). Freigeben, Ablehnen und das
 * Rueckgaengig aus dem Ablehnen-Toast fassten ihn nicht an — und es gibt keinen
 * Heilungspfad: `staleTime` allein loest keinen Refetch aus,
 * `refetchOnWindowFocus` ist projektweit aus, ein `refetchInterval` gibt es
 * nicht, und der Reiterwechsel montiert den Hook nicht neu. Die Zahl am Reiter
 * blieb also den ganzen Aufenthalt lang auf ihrem Anfangswert stehen, waehrend
 * die Liste unmittelbar darunter sich bewegte.
 *
 * Fortschreiben statt invalidieren, gleiches Muster wie
 * `applyDecisionToStats`: eine Entscheidung soll keine drei COUNT-Abfragen
 * ueber sechsstellige Feeds ausloesen (gemessen 108,8 ms warm), und im
 * Schnelldurchlauf faellt eine Entscheidung pro Sekunde.
 *
 * `undefined` bleibt `undefined`. Die Zaehlerabfrage unterscheidet
 * ausdruecklich "unbekannt" von "null Produkte" (siehe
 * `useWorkbenchStatusCounts`); aus einer fehlenden Antwort hier eine gerechnete
 * Zahl zu machen waere genau die Unehrlichkeit, gegen die der Hook gebaut ist.
 */
export function moveStatusCount(
  counts: StatusCounts | undefined,
  from: WorkbenchStatus,
  to: WorkbenchStatus,
  amount = 1,
): StatusCounts | undefined {
  if (!counts) return counts;
  if (from === to || amount === 0) return counts;
  return {
    ...counts,
    [from]: Math.max(0, counts[from] - amount),
    [to]: counts[to] + amount,
  };
}

/**
 * Zaehlt die GELADENEN Zeilen, ueber die noch keine Freigabe-Entscheidung
 * gefallen ist.
 *
 * Grundlage des "alles geprueft"-Leerzustands (siehe
 * `isAllReviewedEmptyState`). Der frueher dort gerechnete Vergleich
 * "Sitzungsentscheidungen === Restmenge" stellte zwei verschiedene
 * Grundmengen gegenueber und meldete den Abschluss, obwohl noch Produkte auf
 * `approval_status='pending_review'` standen — reproduziert ueber: in der
 * Karte freigeben, dann in die Liste wechseln. Diese Funktion fragt nur die
 * eine Menge, um die es geht: die Zeilen, die gerade vor dem Nutzer liegen.
 *
 * Eine Zeile ohne Eintrag in der Map ist unentschieden; ein ausdruecklicher
 * `pending_review`-Eintrag ebenfalls (geschrieben wird heute nur
 * approved/rejected, aber es zaehlt, was ENTSCHIEDEN ist, nicht was in der Map
 * steht). Entscheidungen zu ids, die nicht mehr geladen sind, koennen die
 * Zahl nicht druecken — genau daran ist die alte Rechnung gescheitert.
 *
 * Reine Funktion, damit sie ohne React testbar ist.
 */
export function countUndecidedLoaded(
  items: readonly { id: string }[],
  decisionMap: ReadonlyMap<string, ReviewDecision>,
): number {
  let undecided = 0;
  for (const item of items) {
    const decision = decisionMap.get(item.id);
    if (decision === undefined || decision === 'pending_review') undecided += 1;
  }
  return undecided;
}

/**
 * Patcht ein einzelnes Item im Reviewer-Puffer nach einem erfolgreichen
 * saveChanges-UPDATE.
 *
 * Der Puffer ist seit P0-1 die Anzeige-Quelle im Kartenmodus, nicht mehr der
 * Query-Cache. Ohne diesen Patch verankert saveChanges seine Edits nur noch
 * im (ungenutzten) Query-Cache-Eintrag: ein Nutzer editiert, navigiert weiter
 * (Auto-Save greift, DB ist korrekt), navigiert zurueck — und sieht den alten
 * Wert, weil buffer.items nie aktualisiert wurde.
 *
 * Kein Umsortieren, kein Kuerzen — nur der Item-Patch. Alles andere waere
 * derselbe Skip/Duplikat-Fehler wie das urspruengliche Problem, nur durch
 * einen Edit statt durch Accept/Reject ausgeloest. Reine Funktion, damit sie
 * ohne React testbar ist.
 */
export function patchBufferItem(
  items: WorkbenchProduct[],
  id: string,
  patch: Partial<WorkbenchProduct>,
): WorkbenchProduct[] {
  return items.map((item) => (item.id === id ? { ...item, ...patch } : item));
}

/*
 * BEWUSST NICHT GEBAUT: das Zuruecksetzen auf `needs_reupload` beim
 * Bearbeiten eines bereits hochgeladenen Produkts (Plan Task A5).
 *
 * Die Absicht ist richtig: wer eine freigegebene, bereits ausgelieferte
 * Fassung von Hand korrigiert, macht die Fassung bei Google veraltet, und
 * `uploaded` behauptet danach etwas Falsches.
 *
 * Es geht vom Frontend aus trotzdem nicht. `authenticated` haelt auf
 * `products` ein SPALTEN-GRANT ueber genau elf Spalten (die zehn
 * `optimized_*` plus `approval_status`); `upload_status` ist ausdruecklich
 * NICHT dabei, und `useWorkbench.products-grant.test.ts` pinnt das auch so
 * fest. Postgres lehnt ein `UPDATE` ab, sobald es EINE ungegrantete Spalte
 * beruehrt — der Schreibvorgang waere also nicht teilweise fehlgeschlagen,
 * sondern GANZ, samt der `optimized_*`-Bearbeitung, die der Nutzer
 * tatsaechlich vorgenommen hat. Aus einer fehlenden Markierung waere ein
 * Datenverlust geworden.
 *
 * Die beiden gangbaren Wege sind eine Migration (Spalten-GRANT erweitern)
 * oder eine SECURITY-DEFINER-RPC. Beide liegen ausserhalb dieses Umbaus
 * (Leitplanke 3: keine Migration). Steht als Entscheidung im Log.
 *
 * Was hier sehr wohl gilt und durch Tests gesichert ist: `saveChanges` fasst
 * `approval_status` NIE an. Die Freigabepflicht existiert, damit niemand
 * ungelesenen KI-Text ausliefert, und eine Handkorrektur ist per Definition
 * gelesen. Ein Ruecksetzen auf `pending_review` waere eine Strafe fuer
 * sorgfaeltiges Arbeiten.
 */

/**
 * Identifiziert NUR die Produktmenge, die der Reviewer gerade durchgeht:
 * Feed + Statusreiter + Suche. Jeder der drei Wechsel ist eine andere
 * Produktmenge und muss den Puffer zuruecksetzen (F1; der Reiter kam mit
 * Task A4 dazu — ohne ihn im Token schreibt die Spaetantwort des einen
 * Reiters in den anderen). Der `|` trennt die Teile, damit
 * z.B. Feed `'a'` + Suche `'|b'` nicht zufaellig mit Feed `'a|'` + Suche
 * `'b'` kollidiert (Suchbegriffe mit `|` sind selten, aber nicht
 * ausgeschlossen — der Separator allein schuetzt nicht vor jeder Kollision,
 * wohl aber vor der einzigen, die hier vorkommen kann: `feedId` selbst
 * enthaelt nie ein `|`, UUIDs tun das nicht).
 *
 * ACHTUNG, bewusste Grenze dieser Funktion: die Population ist NICHT dasselbe
 * wie die Puffer-"Session"-Identity, die `appendPage` (reviewBuffer.ts)
 * vergleicht. Zwei Resets koennen dieselbe Population haben — Feed A -> B ->
 * zurueck zu A, oder ein Bulk-Reset waehrend ein Prefetch fuer dieselbe
 * Population noch unterwegs ist (Review-Fund F3, Punkt 1+2). `useWorkbench`
 * haengt deshalb zusaetzlich eine monoton steigende Generation an
 * (`` `${population}|${generation}` ``, siehe dort) — nur DAS ist der Token,
 * den `appendPage` bekommt.
 */
export function computeBufferPopulation(
  feedId: string | null,
  search: string,
  status: WorkbenchStatus = 'pending',
  /**
   * Spec 5.2: der Filter "noch nicht bei Google" ist serverseitig und
   * aendert damit die Menge, die der Server liefert, genau wie die Suche —
   * er gehoert also in die Population, nicht in eine rein clientseitige
   * Sequenz-Kennung (siehe `cardSequenceKey` unten, der `warningsOnly`
   * traegt, weil dieser Filter clientseitig bleibt).
   */
  awaitingUploadOnly: boolean = false,
  /**
   * WP5 A2O-02: die Regel ist serverseitig und aendert die Menge. Ohne Regel
   * bleibt die Zeichenkette zeichengleich zur bisherigen Fassung.
   */
  ruleId: WorkbenchRuleId | null = null,
): string {
  return `${feedId ?? ''}|${status}|${awaitingUploadOnly}|${ruleId ? `rule:${ruleId}|` : ''}${search}`;
}

/**
 * Extrahiert die Generation aus einem Puffer-Identity-Token
 * (`${population}|${generation}`). Generation ist immer der letzte,
 * numerische Abschnitt hinter dem LETZTEN `|` — das bleibt eindeutig, auch
 * wenn `population` selbst (Feed+Suche) weitere `|` enthaelt, weil
 * `computeBufferPopulation` seinerseits schon einen `|` einbaut.
 *
 * Exportiert, weil sie in `bufferQueryKey` einfliesst (Review-Fund: ohne
 * Generation im Query-Key loest ein Reset ohne Cursor-Aenderung — Puffer war
 * schon leer, Cursor schon null — keinen neuen Fetch aus, weil weder der Key
 * noch `enabled` sich sichtbar aendern).
 */
export function parseBufferGeneration(identity: string): number {
  const lastPipe = identity.lastIndexOf('|');
  const raw = lastPipe === -1 ? identity : identity.slice(lastPipe + 1);
  const gen = Number(raw);
  return Number.isFinite(gen) ? gen : 0;
}

/** Kehrseite von `parseBufferGeneration`: alles vor dem letzten `|`. */
function bufferPopulationFromIdentity(identity: string): string {
  const lastPipe = identity.lastIndexOf('|');
  return lastPipe === -1 ? identity : identity.slice(0, lastPipe);
}

export function useWorkbenchPendingCount(_feedId: string | null) {
  const { selectedFeedId } = useSelection();

  return useQuery({
    queryKey: ['workbench-pending-count', selectedFeedId],
    enabled: !!selectedFeedId,
    staleTime: 30_000,
    queryFn: async () => {
      if (!selectedFeedId) return 0;
      const { count, error } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true })
        .eq('feed_id', selectedFeedId)
        .eq('optimization_status', 'completed')
        .eq('approval_status', 'pending_review');
      if (error) throw error;
      return count ?? 0;
    },
  });
}

export function useWorkbench(
  _feedId: string | null,
  filters: WorkbenchFilters = DEFAULT_FILTERS,
  /**
   * Der aktive Statusreiter. Bestimmt die MENGE; `filters` verfeinert sie.
   * Steckt in JEDEM Query-Key und in der Puffer-Identitaet — sonst schreibt
   * die Spaetantwort des einen Reiters in den anderen.
   */
  status: WorkbenchStatus = 'pending',
) {
  const { toast } = useNotifications();
  const queryClient = useQueryClient();
  const { selectedFeedId } = useSelection();

  /**
   * Der Feed, auf dem der Hook GERADE steht.
   *
   * Gebraucht von Aufrufen, die aus einer alten Closure heraus zurueckkommen
   * und deshalb noch den Feed von damals tragen: das "Rueckgaengig" der
   * Sammelfreigabe steht 12 Sekunden, in denen der Nutzer den Feed wechseln
   * kann. Der Ref ist die einzige Moeglichkeit, aus so einem Aufruf heraus
   * zu erkennen, dass die Aufraeumarbeit inzwischen einen fremden Feed traefe.
   */
  const feedIdRef = useRef(selectedFeedId);
  feedIdRef.current = selectedFeedId;

  // `population` identifiziert NUR die Produktmenge (Feed + Suche) — nicht
  // die konkrete Puffer-Session. Zwei Resets auf dieselbe Population sind
  // moeglich (Feed A -> B -> zurueck zu A; ein Bulk-/Auto-Heal-Reset waehrend
  // ein Prefetch fuer dieselbe Population noch unterwegs ist) und muessen
  // sich trotzdem unterscheiden lassen, sonst passiert eine Spaetantwort der
  // VORHERIGEN Session den Identity-Guard in appendPage einfach, weil die
  // Population zufaellig gleich geblieben ist (Review-Fund F3).
  const ruleId = filters.ruleId ?? null;
  const population = computeBufferPopulation(
    selectedFeedId,
    filters.search,
    status,
    filters.awaitingUploadOnly,
    ruleId,
  );

  /**
   * WELCHES Produkt der Cursor meint — nicht welche Position.
   *
   * V2-Befund: `buffer.index` ist eine Position in `products`, und `products`
   * ist NICHT die Serverreihenfolge. Der Server liefert nach
   * `last_optimized_at, id`, die Anzeige sortiert unbedingt nach Score (auch
   * bei `scoreFilter === 'all'`). Jede nachgeladene Seite wird also in die
   * bestehende Liste EINSORTIERT, und alles davor verschiebt sich. Gemessen
   * auf googleshopping.xml: nach dem Anhaengen der zweiten Seite tragen 79 der
   * ersten 100 Positionen ein anderes Produkt; ein Cursor auf Position 90
   * stand danach auf einem fremden Produkt, das eigene lag auf 182.
   *
   * Das Nachladen braucht dafuer kein Zutun des Nutzers (Vorlade-Effect), es
   * trifft also auch jemanden, der gerade in einem Textfeld tippt: der Inhalt
   * wird unter dem Schreibcursor ausgetauscht.
   *
   * Der Anker dreht die Beziehung um — die ID fuehrt, der Index folgt. Jede
   * ABSICHTLICHE Bewegung (Weiter, Zurueck, Sprung aus der Leiste) schreibt
   * hier hin; ein Neusortieren tut es nicht. Genau daran haengt die
   * Unterscheidung "der Nutzer ist woanders hin" gegen "unter ihm wurde
   * umsortiert".
   */
  const cursorAnchorRef = useRef<string | null>(null);
  const anchorJumpRef = useRef(false);

  /**
   * Wird in diesem Render-Durchlauf zurueckgesetzt?
   *
   * Beide Reset-Bloecke unten rufen `setBuffer` WAEHREND des Renders auf.
   * React verwirft die Ausgabe dieses Durchlaufs dann und rendert sofort
   * neu — eine Ref-Zuweisung ist aber keine Ausgabe, sie ueberlebt. Ohne
   * dieses Flag schriebe der verworfene Durchlauf den Anker noch auf das
   * Produkt der ALTEN Sequenz, und die Korrektur zoege den frisch
   * genullten Zeiger im naechsten Durchlauf genau dorthin zurueck.
   */
  let anchorResetInThisRender = false;

  // Generation lebt bewusst NICHT als eigener State neben dem Puffer, sondern
  // wird ausschliesslich aus `buffer.identity` gelesen/geschrieben. Zwei
  // separate States (Population + Generation) haetten bei zwei Resets im
  // selben Tick kollabieren koennen: ein `useCallback`, dessen Closure die
  // Generation aus dem RENDER liest (`generation + 1`), berechnet bei zwei
  // Aufrufen im selben Tick zweimal denselben "naechsten" Wert — die zweite
  // Erhoehung ginge verloren (Review-Fund, Runde 4). Ueber den funktionalen
  // `setBuffer`-Updater liest jeder Reset dagegen den WIRKLICH aktuellen
  // Vorgaengerzustand, auch wenn ein anderer Reset im selben Tick bereits
  // vorher verarbeitet wurde.
  const [buffer, setBuffer] = useState<ReviewBufferState<WorkbenchProduct>>(() =>
    emptyBuffer(`${population}|0`),
  );

  // Populationswechsel-Reset waehrend des Renders, nicht per useEffect: React
  // verwirft diesen Render komplett und rendert sofort mit dem frischen
  // (leeren) Puffer neu, bevor irgendetwas commitet wird. Ein
  // useEffect-basierter Reset liesse einen Frame zu, in dem buffer.items noch
  // die alte Population zeigt, waehrend bufferQueryKey schon auf die neue
  // zeigt — genau das Fenster, in dem ein Accept/Reject das falsche
  // Produkt/Feed treffen koennte (F1).
  if (bufferPopulationFromIdentity(buffer.identity) !== population) {
    setBuffer((prev) => emptyBuffer(`${population}|${parseBufferGeneration(prev.identity) + 1}`));
    // Anker mit abraeumen: er zeigt auf ein Produkt der ALTEN Population, und
    // die Render-Korrektur unten wuerde den Zeiger dorthin ziehen, sobald
    // dasselbe Produkt in der neuen Menge zufaellig wieder auftaucht.
    cursorAnchorRef.current = null;
    anchorResetInThisRender = true;
  }

  // Score-Filter-/Sortierwechsel aendern die sichtbare Kartensequenz komplett,
  // ohne die Population (Feed+Suche) zu beruehren — der Zeiger zeigte danach
  // auf eine Position der ALTEN Sequenz: "Produkt 47 von 12", die Karte stand
  // laengst auf dem letzten Treffer, und "Zurueck" zaehlte 35 Klicks lang
  // unsichtbar herunter. NUR der Index wird genullt, der Puffer selbst gehoert
  // der Population (ein Filterwechsel laedt nichts neu, F4). Im windowed
  // Standardmodus (`scoreFilter === 'all'`) ist die Sequenz von `sortOrder`
  // unabhaengig, deshalb zaehlt der Schluessel dort nur den Filterzustand —
  // ein Sortier-Klick wirft den Nutzer nicht grundlos auf Produkt 1 zurueck.
  // Reset waehrend des Renders, gleiches Muster wie der Populationswechsel
  // oben und aus demselben Grund (kein commiteter Frame mit altem Index).
  // `warningsOnly` zaehlt hier mit, weil es die sichtbare Sequenz genauso
  // umwirft wie eine Punkte-Pille: der Menueeintrag "N mit Hinweisen zuerst
  // ansehen" schaltet ihn von aussen ein, und ohne den Reset stuende der
  // Zeiger danach auf einer Position der ungefilterten Menge — genau die
  // "Produkt 47 von 12"-Unehrlichkeit, gegen die dieser Block steht.
  const cardSequenceKey =
    filters.scoreFilter === 'all' && !filters.warningsOnly
      ? 'all'
      : `${filters.scoreFilter}|${filters.warningsOnly ? 'warn' : 'any'}|${filters.sortOrder}`;
  const [displaySequenceKey, setDisplaySequenceKey] = useState(cardSequenceKey);
  if (displaySequenceKey !== cardSequenceKey) {
    setDisplaySequenceKey(cardSequenceKey);
    setBuffer((prev) => (prev.index === 0 ? prev : { ...prev, index: 0 }));
    cursorAnchorRef.current = null;
  
    anchorResetInThisRender = true;
  }

  const currentIndex = buffer.index;

  // Gemeinsamer Reset-Pfad fuer die beiden Faelle OHNE Populationswechsel:
  // Bulk-Aktion und Auto-Heal (unten). Erhoeht dieselbe Generation wie der
  // Populationswechsel-Reset oben, damit appendPage jede in-flight Antwort
  // einer VOR diesem Reset gestarteten Anfrage verwirft — auch wenn Feed und
  // Suche unveraendert bleiben (Review-Fund F3, Punkt 2: ein Prefetch fuer
  // Seite 2 kann laufen, waehrend "Alle Guten uebernehmen" geklickt wird).
  const resetBuffer = useCallback(() => {
    // Der Puffer beginnt von vorn — ein Anker auf ein Produkt der alten Menge
    // wuerde den Zeiger sonst an eine Stelle ziehen, die es nicht mehr gibt.
    cursorAnchorRef.current = null;
    setBuffer((prev) => emptyBuffer(`${population}|${parseBufferGeneration(prev.identity) + 1}`));
  }, [population]);

  // Two independent maps:
  // 1. reviewDecisionMap: tracks accept/reject decisions (written to DB)
  const [reviewDecisionMap, setReviewDecisionMap] = useState<Map<string, ReviewDecision>>(new Map());
  // 2. editedSet: tracks which products the user manually edited (yellow badge)
  const [editedSet, setEditedSet] = useState<Set<string>>(new Set());

  // Beide Mengen gehoeren zum FEED, nicht zur `population` (Feed+Suche). Ein
  // Feedwechsel remountet den Hook nicht — `useWorkbench` haengt an der
  // Werkbank-Seite, `selectedFeedId` kommt aus dem SelectionContext. Ohne
  // diesen Reset zaehlten `sessionAccepted`/`sessionRejected` weiter die
  // Entscheidungen des VORHERIGEN Feeds mit, und der "alles
  // geprueft"-Leerzustand beschriftete sich mit der Summe beider Feeds (3 auf
  // Feed A + 2 auf Feed B ergaben 5 statt 2). `editedSet` ebenso: sonst traegt
  // ein Produkt des neuen Feeds das "bearbeitet"-Abzeichen, sobald seine id
  // zufaellig schon einmal editiert wurde.
  //
  // Bewusst NICHT an `population` gekoppelt, obwohl der Puffer das ist: ein
  // Suchwechsel ist dieselbe Freigabe-Sitzung auf demselben Feed. Wer sucht,
  // entscheidet, und dann das Suchfeld leert, wuerde sonst seine eigenen
  // Entscheidungen aus der Anzeige verlieren.
  //
  // Reset waehrend des Renders, gleiches Muster wie der Puffer-Reset oben und
  // aus demselben Grund: React verwirft diesen Render und rendert sofort mit
  // den leeren Mengen neu, bevor irgendetwas commitet wird. Ein useEffect
  // liesse einen fertig commiteten Frame stehen, in dem die Zaehler noch die
  // Summe beider Feeds zeigen.
  //
  // V2-Befund, Nachtrag zum Absatz darueber: fuer den REITER gilt das Gegenteil
  // von dem, was fuer die Suche gilt. `countUndecidedLoaded` rechnet gegen die
  // rohe Sitzungs-Map, waehrend die Anzeige ueber `getReviewDecision` mit
  // Reiter-Rueckfall liest. Nimmt man die Map mit in den Reiter "Freigegeben",
  // sind dort genau die Zeilen als entschieden vermerkt, die der Nutzer gerade
  // freigegeben hat — `undecidedLoadedCount` faellt auf 0 und die Werkbank
  // meldet "Alles ueberprueft" ueber einer nicht leeren Liste. Das ist der
  // Normalfall, nicht der Rand: 43 von 45 Feeds mit offenen Produkten haben
  // ueberhaupt keine freigegebenen.
  //
  // Im neuen Reiter braucht es die Map auch gar nicht: dort ist der Reiterwert
  // selbst die Entscheidung.
  const decisionScope = `${selectedFeedId ?? ''}|${status}`;
  const [decisionScopeState, setDecisionScopeState] = useState(decisionScope);
  if (decisionScopeState !== decisionScope) {
    setDecisionScopeState(decisionScope);
    setReviewDecisionMap(new Map());
    setEditedSet(new Set());
    // K7-Befund, uebertragen auf die serverseitigen Zahlen der Sammelfreigabe:
    // sie sind 30 s gecacht und ueberleben einen Feed-/Reiterwechsel. Nach
    // A -> B -> A binnen 30 s nennt das Menue sonst die Zahlen von vor dem
    // Wechsel, obwohl in B zwischenzeitlich freigegeben worden sein kann. Die
    // Marke sorgt dafuer, dass der Ruecksprung frisch zaehlt ('none': der
    // WECHSEL selbst laedt nichts).
    queryClient.invalidateQueries({ queryKey: ['workbench-bulk-summary'], refetchType: 'none' });
  }

  // --- Server-side total count (used for card view progress + the honest
  // list-view counter, Task 6/P0-4: the list no longer loads the whole feed,
  // so it needs an independent feed-wide count to compare against). ---
  // K5-Befund: der Fehler dieser Zaehl-Query wurde kommentarlos verworfen und
  // fiel ueber `?? 0` auf die bloss GELADENE Menge zurueck — "Produkt X von
  // 100" statt "von 3.830", ohne Heilungspfad (Entscheidungen invalidieren
  // mit refetchType 'none', kein Focus-Refetch). Deshalb: isError wird
  // exportiert (die Anzeigen lassen die Gesamtzahl dann WEG statt zu luegen),
  // und im Fehlerzustand pollt die Query alle 30 s nach — sie heilt selbst.
  const { data: serverTotal, isError: serverTotalFailed } = useQuery({
    // Spec 5.2: `awaitingUploadOnly` steht im Key wie `filters.search` — beide
    // aendern serverseitig, WELCHE Menge gezaehlt wird.
    queryKey: [
      'workbench-server-total',
      selectedFeedId,
      status,
      filters.search,
      filters.awaitingUploadOnly,
      // Nur MIT Regel ein weiteres Segment: ohne sie bleiben die Keys der
      // Bestandsabfragen (und ihre Invalidierungen) zeichengleich.
      ...(ruleId ? [ruleId] : []),
    ],
    enabled: !!selectedFeedId,
    staleTime: 30_000,
    refetchInterval: (query) => (query.state.status === 'error' ? 30_000 : false),
    queryFn: async () => {
      if (!selectedFeedId) return 0;
      let query = supabase
        .from('products')
        .select('*', { count: 'exact', head: true })
        .eq('feed_id', selectedFeedId)
        .eq('optimization_status', 'completed')
        .eq('approval_status', approvalValueFor(status));
      if (filters.awaitingUploadOnly) {
        query = query.or(AWAITING_UPLOAD_OR);
      }
      if (ruleId) {
        // Siehe die Begruendung an der ersten Fundstelle (`fetchProductPage`):
        // ein rohes Array-Argument wird von postgrest-js als Postgres-Array
        // serialisiert, nicht als jsonb.
        query = query.contains('validation_warnings', JSON.stringify(ruleContainsFilter(ruleId)));
      }
      if (filters.search.trim()) {
        const term = `%${filters.search.trim()}%`;
        query = query.or(`title.ilike.${term},brand.ilike.${term},optimized_title.ilike.${term}`);
      }
      const { count, error } = await query;
      if (error) throw error;
      return count ?? 0;
    },
  });

  // --- Zahlen der Sammelfreigabe (Etappe 4, Spec 9.1/9.3) ---
  // Loest die frueher hier stehende Kandidaten-Query ab
  // (`fetchGoodPendingCandidateIds`): eine Keyset-Paginierung in 1000er-Seiten
  // mit client-seitiger Score-Auswertung, auf dem groessten Feed (61.022
  // offene Produkte) also 62 sequentielle Requests, nur um eine Zahl an einen
  // Menueeintrag zu schreiben. Jetzt EIN Aufruf, der alle fuenf Zahlen aus
  // demselben Durchgang liefert - so koennen Menuezahl und Dialogzahl nicht
  // gegeneinander driften, und der Dialog ist genau die Stelle, an der die
  // Zahl eine Entscheidung traegt.
  //
  // Unabhaengig von viewMode/scoreFilter/sortOrder: gezaehlt wird der ganze
  // Feed, nicht der geladene Ausschnitt. Die aktive Suche steht im Key (P0-3),
  // sonst servierte eine Suchaenderung die Zahlen der vorherigen Suche aus dem
  // Cache - und die Sammelfreigabe traefe eine andere Menge als angezeigt.
  //
  // Task A4: die Sammelfreigabe bleibt dem Reiter "Offen" vorbehalten, deshalb
  // das `status`-Gate im `enabled`. Es steht bewusst NICHT im Key: die Zahlen
  // beschreiben immer die offenen Produkte, ein Reiterwechsel macht sie also
  // nicht falsch, nur unsichtbar.
  const bulkSummaryQueryKey = ['workbench-bulk-summary', selectedFeedId, filters.search];
  // K5-Lehre, uebernommen vom abgeloesten Knopf: `data` faellt NICHT auf eine
  // Null-Zusammenfassung zurueck. Ein Fehler der Abfrage und "null offene
  // Produkte" sind verschiedene Aussagen; wer sie gleichsetzt, laesst die
  // Handlung kommentarlos verschwinden. `isError` + `refetch` gehen nach oben,
  // damit die Oberflaeche den Fehlschlag benennen und einen Retry anbieten
  // kann.
  const {
    data: bulkSummary = null,
    isError: bulkSummaryFailed,
    refetch: refetchBulkSummaryQuery,
  } = useQuery<BulkSummary | null>({
    queryKey: bulkSummaryQueryKey,
    enabled: !!selectedFeedId && status === 'pending',
    staleTime: 30_000,
    queryFn: async () => {
      if (!selectedFeedId) return null;
      const params = { p_feed_id: selectedFeedId, p_search: filters.search || null };
      const { data, error } = await supabase.rpc('workbench_bulk_summary', params);
      if (error) throw error;

      // NULL statt Nullzeilen: die RPC gibt bei fremdem Feed GAR KEINE Zeile
      // zurueck (bewusst ununterscheidbar von "existiert nicht", kein
      // Existenz-Orakel). Eine Zusammenfassung aus lauter Nullen daraus zu
      // bauen waere eine Behauptung ueber Daten, die wir nie gesehen haben -
      // und die Oberflaeche zeigte "0 offene Produkte" ueber einem Feed, der
      // voll davon ist.
      const row = data?.[0];
      if (!row) return null;

      return {
        openCount: Number(row.open_count ?? 0),
        safeCount: Number(row.safe_count ?? 0),
        warningCount: Number(row.warning_count ?? 0),
        lowScoreCount: Number(row.low_score_count ?? 0),
        unscoredCount: Number(row.unscored_count ?? 0),
      };
    },
  });

  // Stabile Referenz nach aussen: `refetch` von React Query nimmt Optionen
  // entgegen, der Vertrag in `bulkRelease.ts` ist ein parameterloser Aufruf.
  // Ohne die Kapselung landete ein Klick-Event als Options-Objekt in refetch.
  const refetchBulkSummary = useCallback(() => {
    void refetchBulkSummaryQuery();
  }, [refetchBulkSummaryQuery]);

  // --- List-view / score-filtered: paginated fetch (P0-4, Task 6) ---
  // Vorher lud dieser Pfad ueber `fetchAllProductsWithScores` den GESAMTEN
  // Feed (bei 69k offenen Produkten ~129 MB in 70 sequentiellen Requests) und
  // rendert alles unvirtualisiert. Jetzt laedt er seitenweise ueber denselben
  // Keyset-Cursor wie der Karten-Puffer (Task 3); WorkbenchListView (Task 6)
  // virtualisiert die Zeilen und fordert per `fetchNextPage` nach.
  //
  // Review-Fund F4: `scoreFilter`/`sortOrder` gehoeren NICHT in den Query-Key.
  // `fetchProductPage` kennt beide Parameter gar nicht (nur feedId/search/
  // cursor/limit) — sie erreichen den Server nie. `applyScoreFilterAndSort`
  // laeuft rein clientseitig im `fullProducts`-useMemo unten. Waeren sie Teil
  // des Keys, wuerfe ein Pillen-Klick (z.B. "Kritisch" -> "Gut") die komplette
  // Infinite-Query weg und der Nutzer faengt bei Seite 1 neu an, obwohl exakt
  // dieselben Rohdaten schon geladen waren.
  //
  // `awaitingUploadOnly` gehoert dagegen sehr wohl in den Key (Spec 5.2): der
  // Filter ist serverseitig, `fetchProductPage` kennt ihn, und ohne ihn im
  // Key zeigte ein Umschalten die Seiten des ANDEREN Zustands aus dem Cache.
  const fullQueryKey = [
    'workbench-products',
    selectedFeedId,
    status,
    filters.search,
    filters.awaitingUploadOnly,
    ...(ruleId ? [ruleId] : []),
  ];

  const {
    data: infiniteData,
    isLoading: isFullLoading,
    error: fullError,
    // H6-F4: offline pausiert React Query die Abfrage (networkMode 'online'),
    // statt sie fehlschlagen zu lassen. `error` bleibt dann null, `isLoading`
    // ebenfalls false (isPending && isFetching) — ohne dieses Flag ist ein
    // nicht ausfuehrbarer Abruf von einer ERFOLGREICHEN leeren Antwort nicht
    // unterscheidbar, und die Werkbank rendert den luegenden Leerzustand.
    isPaused: isFullPaused,
    refetch: refetchFull,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: fullQueryKey,
    enabled: !!selectedFeedId,
    initialPageParam: null as WorkbenchCursor | null,
    staleTime: 30_000,
    queryFn: async ({ pageParam }) => {
      if (!selectedFeedId) return [];
      return fetchProductPage(
        selectedFeedId,
        filters.search,
        pageParam,
        REVIEW_PAGE_SIZE,
        status,
        filters.awaitingUploadOnly,
        ruleId,
      );
    },
    // F7/F9: extrahierte, eigenstaendig getestete Funktion statt einer
    // zweiten inline Kopie der Cursor-Ableitung (siehe deren Doc-Kommentar).
    // Explizit mit EINEM Argument aufgerufen, nicht per Referenz durchgereicht
    // -- `useInfiniteQuery` ruft `getNextPageParam(lastPage, allPages, ...)`,
    // und `allPages` (ein Array) wuerde sonst auf den optionalen `pageSize`-
    // Parameter durchschlagen.
    getNextPageParam: (lastPage: WorkbenchProduct[]) => getNextPageParamForProducts(lastPage),
  });

  // Rohe, ungefilterte Anzahl bereits geladener Zeilen — Basis fuer den
  // ehrlichen Zaehler in WorkbenchListView ("X von Y offenen Produkten
  // geladen"). Wird VOR applyScoreFilterAndSort gezaehlt, sonst saehe ein
  // aktiver Score-Filter faelschlich wie "wenig geladen" statt "wenig passt".
  const listLoadedCount = infiniteData?.pages.reduce((sum, page) => sum + page.length, 0) ?? 0;

  // P0-4 Review-Runde 2, Fund N1: Deckel-Zustand fuers automatische
  // Nachladen. `unlockedAutoLoadBatches` zaehlt, wie oft der Nutzer per
  // Button ("Weiter suchen") ein weiteres AUTO_LOAD_ROW_CAP-Batch
  // freigegeben hat — an Feed, Suche und `awaitingUploadOnly` gebunden, NICHT
  // an scoreFilter/sortOrder: die beiden aendern die zugrunde liegende Rohmenge
  // nicht (siehe F4) und filtern nur clientseitig nach.
  // `awaitingUploadOnly` gehoert dagegen dazu, denn er steht als `.or(...)` in
  // der Serverabfrage und aendert damit genau diese Rohmenge (Spec 5.2). Ohne
  // ihn startete der Deckel nach dem Umschalten mit den Freigaben der
  // vorherigen, groesseren Menge.
  const [unlockedAutoLoadBatches, setUnlockedAutoLoadBatches] = useState(0);
  useEffect(() => {
    setUnlockedAutoLoadBatches(0);
  }, [selectedFeedId, filters.search, filters.awaitingUploadOnly, ruleId]);

  // Nur relevant fuer die paginierten Pfade (Liste / score-gefilterte
  // Karte) — im windowed Kartenmodus bleibt listLoadedCount 0 (Infinite-
  // Query dort disabled), autoLoadCapReached also immer false, was dort
  // ohnehin nirgends abgefragt wird.
  const autoLoadCapReached = isAutoLoadCapReached(listLoadedCount, unlockedAutoLoadBatches);
  // K6: Fehler klemmt die Navigation wie der Deckel (Begruendung bei canGoNext).
  const navClamped = autoLoadCapReached || Boolean(fullError);

  // C3 (Browser-E2E-Fund, Runde 4): ein Klick auf "Weiter laden" hob den
  // Deckel nur logisch an (unlockedAutoLoadBatches+1) und verliess sich
  // darauf, dass die Nachlade-Effects (hier + in WorkbenchListView.tsx) die
  // Aenderung an `autoLoadCapReached` von selbst bemerken und nachladen.
  // Empirisch belegt: das passiert NICHT sofort — erst ein Scroll-Event
  // (das den Virtualizer ohnehin neu rendert) loeste den Fetch tatsaechlich
  // aus, bis zu 6s Stille nach dem Klick. `fetchNextPage()` direkt im Klick-
  // Handler behebt das an der Quelle, fuer BEIDE Aufrufer (Liste ueber
  // `onUnlockMoreAutoLoad`, score-gefilterter Kartenmodus ueber denselben
  // `unlockMoreAutoLoad`) in einem Schritt. `fetchNextPage` guards intern
  // gegen doppelte/unnoetige Aufrufe (kein Effekt ohne `hasNextPage` bzw.
  // waehrend bereits eine Seite laedt), ein zusaetzlicher Aufruf hier ist
  // also sicher, selbst wenn ein Effect zufaellig im selben Tick ebenfalls
  // feuert.
  const unlockMoreAutoLoad = useCallback(() => {
    setUnlockedAutoLoadBatches((prev) => prev + 1);
    fetchNextPage();
  }, [fetchNextPage]);

  // applyScoreFilterAndSort wandert bewusst AUS der Query heraus: sie filtert
  // und sortiert jetzt nur noch die bereits geladenen Seiten, nicht mehr den
  // gesamten Feed. Bewusste Verhaltensaenderung (siehe Task-6-Brief) — die
  // Liste zeigt die beste Auswahl DES GELADENEN, der ehrliche Zaehler gleicht
  // das fuer den Nutzer aus.
  const fullProducts = useMemo(() => {
    const flat = (infiniteData?.pages.flat() ?? []) as WorkbenchProduct[];
    return flat.length > 0 ? applyScoreFilterAndSort(flat, filters) : flat;
  }, [infiniteData, filters]);

  // Selbstheilung gegen einen veralteten Zaehler.
  //
  // Portiert aus dem geloeschten Fensterpfad (dort haing sie an
  // `buffer.exhausted`): keine geladene Zeile, nichts mehr nachzuladen, aber
  // `serverTotal` behauptet weiter Produkte. Der Zustand heilt nicht von
  // selbst — `refetchOnWindowFocus` ist projektweit aus
  // (`src/lib/queryClient.ts`) und die Zaehler-Query hat kein
  // `refetchInterval`; die 30 Sekunden `staleTime` laufen ab, ohne dass
  // jemand neu holt. Ohne Zutun bliebe eine leere Flaeche neben einer Zahl
  // groesser null stehen, bis der Nutzer navigiert oder neu laedt.
  //
  // Genau EINMAL pro Auftreten, festgehalten am Ref: die Zaehler-Query prueft
  // dasselbe Praedikat wie die Seitenabfrage, ein zweiter Anlauf braechte also
  // dasselbe Ergebnis — und eine Invalidierung, die ihre eigene Bedingung
  // nicht aufloest, waere eine Schleife. Der Ref faellt zurueck, sobald der
  // Zustand verlassen wird.
  const staleServerTotalHealedRef = useRef(false);
  useEffect(() => {
    const emptyWithStaleCount =
      !isFullLoading && fullProducts.length === 0 && !hasNextPage && (serverTotal ?? 0) > 0;
    if (!emptyWithStaleCount) {
      staleServerTotalHealedRef.current = false;
      return;
    }
    if (staleServerTotalHealedRef.current) return;
    staleServerTotalHealedRef.current = true;
    queryClient.invalidateQueries({ queryKey: ['workbench-server-total', selectedFeedId] });
  }, [isFullLoading, fullProducts.length, hasNextPage, serverTotal, selectedFeedId, queryClient]);

  // --- Unified interface ---
  // Seit Task B1 gibt es genau EINEN Ladepfad. Die frueheren Ternaere standen
  // hier ueber `useWindowedPath` und haben zweimal Zustandsfehler erzeugt:
  // zwei Zwischenspeicher, zwei Zaehlweisen, ein Umschalter dazwischen.
  const products = fullProducts;
  const isLoading = isFullLoading;
  const error = fullError;
  const isPaused = isFullPaused;
  const refetch = refetchFull;
  const queryKey = fullQueryKey;

  // total und currentIndex (= buffer.index) stammen im windowed Pfad aus zwei
  // unabhaengigen Quellen: serverTotal ist eine 30s-alte COUNT-Abfrage, die
  // z.B. nach einer Bulk-Aktion sinkt; buffer.items waechst dagegen nur
  // innerhalb der Sitzung. Ohne diesen Floor koennte total unter die bereits
  // geladene Puffer-Groesse fallen ("47 von 40") und die Fortschrittsanzeige
  // wie auch canGoNext (unten) wuerden Produkte verstecken, die tatsaechlich
  // noch im Puffer liegen (F5). Semantik: total ist "das Maximum aus dem, was
  // der Server zuletzt gemeldet hat, und dem, was diese Sitzung bereits
  // geladen hat" — die Anzeige unterschreitet den erreichten Fortschritt nie,
  // kann nach einer Bulk-Aktion aber kurz ueberzeichnen, bis der naechste
  // serverTotal-Refetch (30s) das echte Bild nachzieht.
  // Der Score-Filter greift rein CLIENTSEITIG auf den geladenen Seiten. Die
  // Server-Zaehlung kennt ihn nicht und beschreibt die sichtbare Menge dann
  // nicht mehr: sie meldete 5, waehrend 3 Zeilen passen — genau die
  // "Produkt 47 von 12"-Unehrlichkeit. Bei aktivem Filter ist die geladene,
  // gefilterte Menge die einzige Zahl, die stimmt.
  //
  // Ohne Filter ist `serverTotal` dagegen die Wahrheit ueber den Feed, und der
  // Boden gegen `products.length` haelt F5 fest: die 30-Sekunden-COUNT-Abfrage
  // kann nach einer Bulk-Aktion sinken, die geladene Menge waechst dagegen nur
  // innerhalb der Sitzung. Ohne Boden faellt `total` unter das bereits
  // Geladene ("47 von 40") und `canGoNext` versteckt vorliegende Produkte.
  // `warningsOnly` steht aus demselben Grund in der Bedingung wie der
  // Score-Filter: auch er greift rein clientseitig, die Server-Zaehlung kennt
  // ihn nicht. Ohne ihn meldete der Kopf nach einem Klick auf "N mit
  // Hinweisen zuerst ansehen" weiterhin die feed-weite Zahl ueber einer
  // sichtbar viel kleineren Liste.
  const total =
    filters.scoreFilter === 'all' && !filters.warningsOnly
      ? Math.max(serverTotal ?? 0, products.length)
      : products.length;

  /**
   * Der Index folgt dem Anker (siehe `cursorAnchorRef`).
   *
   * Steht das angekerte Produkt woanders als der Zeiger, hat sich unter dem
   * Nutzer die Reihenfolge geaendert — dann zieht der Zeiger nach, nicht
   * umgekehrt. Findet sich der Anker gar nicht mehr (entschieden, weggefiltert,
   * geloescht), bleibt es beim geklemmten Zeiger: dann rueckt das naechste
   * Produkt nach, und genau das ist nach einer Entscheidung gewollt.
   *
   * Korrektur waehrend des Renders, gleiches Muster wie der
   * Populationswechsel-Reset weiter oben und aus demselben Grund — ein
   * commiteter Frame mit dem falschen Produkt ist genau der Frame, in dem
   * jemand weitertippt.
   */
  const clampedIndex = Math.min(currentIndex, Math.max(0, products.length - 1));

  /**
   * Hat sich der Zeiger seit dem letzten Durchlauf bewegt? Dann war es der
   * Nutzer (Weiter, Zurueck, Sprung, Entscheidung) — der Anker folgt ihm.
   *
   * Steht der Zeiger still und das angekerte Produkt liegt trotzdem woanders,
   * wurde unter dem Nutzer umsortiert — dann zieht der Zeiger nach.
   *
   * Diese Unterscheidung am Zeiger statt in den Bewegungsfunktionen zu
   * treffen ist kein Stilfrage: `advanceToNext` kann im selben Render
   * mehrfach feuern (gebuendelte Aufrufe, gehaltene Pfeiltaste), und eine
   * dort gerechnete Zielposition waere die des ersten Schritts.
   */
  const lastSeenIndexRef = useRef(currentIndex);
  const indexMovedByCaller = currentIndex !== lastSeenIndexRef.current;
  lastSeenIndexRef.current = currentIndex;

  // K4-Befund: ein Klick traegt eine IDENTITAET, goToProduct rechnete sie
  // aber in eine Position der Klick-Zeitpunkt-Liste um. Wird die Bewegung
  // (durchs Speicher-Tor geparkt) erst NACH einer Seitenankunft ausgefuehrt,
  // zeigt der stale Index nach dem Re-Sort auf ein FREMDES Produkt — und der
  // Anker uebernahm dessen ID dauerhaft. Das Flag laesst den Anker fuer den
  // Render nach einem Identitaets-Sprung FUEHREN (statt zu adoptieren);
  // konsumiert wird es erst nach dem Commit (Effect unten) — ein verworfener
  // Render darf die Fuehrung nicht aufbrauchen (Lehre aus Bekannt Nr. 24).

  const anchoredIndex = cursorAnchorRef.current
    ? products.findIndex((p) => p.id === cursorAnchorRef.current)
    : -1;

  // Findet sich der Anker gar nicht mehr (entschieden, weggefiltert,
  // geloescht), bleibt es beim geklemmten Zeiger: dann rueckt das naechste
  // Produkt nach, und genau das ist nach einer Entscheidung gewollt.
  const nachziehen = (anchorJumpRef.current || !indexMovedByCaller) && anchoredIndex >= 0;
  const effectiveIndex = nachziehen ? anchoredIndex : clampedIndex;
  if (nachziehen && anchoredIndex !== currentIndex) {
    setBuffer((prev) => (prev.index === anchoredIndex ? prev : { ...prev, index: anchoredIndex }));
  }

  const currentProduct = products[effectiveIndex] ?? null;

  // Nachziehen des Ankers auf das, was tatsaechlich steht. Ist der Anker
  // gueltig, aendert das nichts; ist er verfallen, uebernimmt er das
  // nachgerueckte Produkt. In einem verworfenen Render-Durchlauf bleibt er
  // unangetastet (siehe `anchorResetInThisRender`).
  if (!anchorResetInThisRender) {
    cursorAnchorRef.current = currentProduct?.id ?? null;
  }

  // Konsum des Sprung-Flags NACH dem Commit — Effects laufen in verworfenen
  // Render-Durchlaeufen nicht, die Fuehrung uebersteht sie also.
  useEffect(() => {
    anchorJumpRef.current = false;
  });

  // Der nach aussen gegebene Index traegt im paginierten Kartenpfad dieselbe
  // Klemmung wie currentProduct eine Zeile hoeher: schrumpft die gefilterte
  // Menge unter den rohen Zeiger (Refetch nach Bulk-Aktion, Cache-Ablauf),
  // stuende im Kopf sonst "Produkt 47 von 12", waehrend die Karte laengst
  // products[11] rendert. Der rohe buffer.index bleibt intern erhalten
  // (Nachlade-Effect unten, canGoPrev). Seit dem Anker ist das `effectiveIndex`
  // — die Position, an der das angezeigte Produkt WIRKLICH steht. Sonst zaehlte
  // der Kopf eine Position, an der ein anderes Produkt liegt.
  const displayIndex = effectiveIndex;

  // Nachladen beim Durchblaettern.
  //
  // Ursprung (P0-4 F2): der score-gefilterte Kartenmodus teilte sich die
  // Infinite-Query mit dem Listenmodus, hatte aber KEINEN eigenen
  // Nachlade-Aufrufer — nur `WorkbenchListView` ruft `fetchNextPage`, und die
  // rendert nur in der Liste.
  //
  // Seit Task B1 gilt das fuer JEDE Kartenansicht, nicht nur die gefilterte:
  // der frueher zustaendige Fensterpfad hat sich selbst nachgeladen, den gibt
  // es nicht mehr. Waere der Effect weiter auf `scoreFilter !== 'all'`
  // gescopt, endete die Werkbank im Normalfall stillschweigend bei Produkt
  // 100, waehrend der Zaehler weiter 3.777 behauptet: "Weiter" bleibt
  // klickbar und es passiert nichts.
  //
  // Feuert auch bei `products.length === 0` (Filter matcht auf der aktuellen
  // Seite nichts): `currentIndex(0) >= 0-10` ist wahr, laedt also sofort nach,
  // statt in einer leeren Ansicht haengen zu bleiben (siehe auch F3 — der
  // "alles geprueft"-Leerzustand darf genau dann nicht feuern).
  //
  // Review-Runde 2, Fund N1: `autoLoadCapReached` deckelt genau diesen
  // Automatismus. Ohne Deckel scannt ein Filter, der nie matcht, den ganzen
  // Feed sequentiell durch (~690 Requests bei 69k Produkten). Der Deckel
  // stoppt das automatische Nachladen bei AUTO_LOAD_ROW_CAP Zeilen;
  // `unlockMoreAutoLoad` (Button in der UI) gibt das naechste Batch frei.
  useEffect(() => {
    // K5-Befund: ohne den error-Guard war das eine unbegrenzte Dauerschleife —
    // der isFetchingNextPage-Flip nach einem FEHLGESCHLAGENEN fetchNextPage
    // triggert den Effect erneut, die Bedingung ist unveraendert wahr, und er
    // haemmert dieselbe scheiternde Seite (je Zyklus 3 Serverversuche). Nach
    // einem Fehler laedt nur noch der Mensch nach ("Mehr laden" / Retry).
    if (fullError || !hasNextPage || isFetchingNextPage || autoLoadCapReached) return;
    if (currentIndex >= products.length - CARD_SCORE_FILTER_PREFETCH) {
      fetchNextPage();
    }
  }, [
    filters.scoreFilter,
    // Aus demselben Grund wie `scoreFilter`: schaltet der Nutzer auf
    // Beanstandungen um und die geladene Seite traegt keine, muss sofort
    // nachgeladen werden statt in einer leeren Ansicht zu stehen.
    filters.warningsOnly,
    fullError,
    hasNextPage,
    isFetchingNextPage,
    autoLoadCapReached,
    currentIndex,
    products.length,
    fetchNextPage,
  ]);

  // Zaehlen die ganze Sitzung, unabhaengig davon, ob das entschiedene Produkt
  // noch geladen ist. Bewusst so: sie beschriften den Leerzustand ("N
  // freigegeben, M abgelehnt"). Sie sind KEINE Grundlage fuer die Frage, ob
  // noch etwas offen ist — genau diese Verwechslung war der Produktionsfehler,
  // siehe isAllReviewedEmptyState und countUndecidedLoaded.
  const sessionAccepted = Array.from(reviewDecisionMap.values()).filter((s) => s === 'approved').length;
  const sessionRejected = Array.from(reviewDecisionMap.values()).filter((s) => s === 'rejected').length;

  // Die Grundlage des "alles geprueft"-Leerzustands: wie viele der AKTUELL
  // GELADENEN Zeilen sind noch offen? Laeuft ueber `products` und damit
  // automatisch ueber die richtige Menge in beiden Pfaden — im Kartenpfad
  // `buffer.items` (behaelt das freigegebene Produkt), im Listenpfad die
  // serverseitig auf `pending_review` gefilterten Seiten.
  const undecidedLoadedCount = useMemo(
    () => countUndecidedLoaded(products, reviewDecisionMap),
    [products, reviewDecisionMap],
  );

  // "Kann es weitere geben, die noch nicht geladen sind?" Zwei Quellen, weil
  // die beiden Pfade verschieden nachladen: der Puffer weiss selbst, ob er am
  // Ende ist (`exhausted` wird in appendPage aus "Seite kleiner als pageSize"
  // gesetzt), waehrend `hasNextPage` fuer den Kartenpfad ausdruecklich
  // unbrauchbar ist — `scopeHasNextPageForWindowedPath` setzt es dort hart auf
  // false, sonst leckt der seit F4 geteilte Infinite-Query-Cache herein (N2).
  // Der paginierte Pfad hat umgekehrt keinen Puffer und bleibt bei
  // `hasNextPage`; das haelt F3 fest (100 geladen und alle entschieden ist
  // nicht "Feed zu Ende").
  const mayHaveMore = !!hasNextPage;

  /**
   * Ids der geladenen Zeilen, die bereits bei Google liegen
   * (`upload_status === 'uploaded'`).
   *
   * Als Ref und nicht als Memo, weil die Entscheidungs-Callbacks
   * (`acceptProduct` / `rejectProduct` / `undoReject`) sonst bei jeder
   * Puffer-Aenderung neue Identitaeten bekaemen — sie haengen an den
   * Karten-Knoepfen. Gelesen wird nur im Klick-Moment, da ist die Ref durch
   * den Effect unten aktuell.
   *
   * Fehlt eine Id (Zeile nicht mehr geladen), gilt "nicht hochgeladen": das ist
   * das Verhalten von vor diesem Feld, und der naechste Refetch korrigiert es.
   */
  const uploadedLoadedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const next = new Set<string>();
    for (const product of products) {
      if (product.upload_status === 'uploaded') next.add(product.id);
    }
    uploadedLoadedIdsRef.current = next;
  }, [products]);

  /**
   * Die Entscheidung zu einem Produkt: die der SITZUNG, sonst die des Reiters.
   *
   * Der Rueckfall war bis Task C5 hart `'pending_review'`. Im Reiter "Offen"
   * ist das richtig; in "Freigegeben" und "Abgelehnt" hat kein Produkt eine
   * Sitzungsentscheidung, und die Statuspille behauptete dort durchgehend
   * "Offen" fuer Produkte, die nachweislich freigegeben oder abgelehnt sind —
   * genau die Unehrlichkeit, gegen die die Pille gebaut wurde (Task A6). Im
   * Browser gefunden, nicht von den Tests.
   *
   * Der Reiterwert ist der richtige Rueckfall, weil die Abfrage genau darauf
   * filtert: was hier geladen ist, TRAEGT diesen `approval_status`. Die
   * Sitzung bleibt vorrangig, sie ist aktueller als die geladene Zeile.
   */
  const getReviewDecision = useCallback((productId: string): ReviewDecision => {
    return reviewDecisionMap.get(productId) ?? approvalValueFor(status);
  }, [reviewDecisionMap, status]);

  const wasEdited = useCallback((productId: string): boolean => {
    return editedSet.has(productId);
  }, [editedSet]);

  const markEdited = useCallback((productId: string) => {
    setEditedSet((prev) => {
      if (prev.has(productId)) return prev;
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  }, []);

  // Kein Anker-Schreiben hier: die Bewegungen laufen ueber den funktionalen
  // Updater und koennen im selben Render mehrfach feuern (Tastenwiederholung,
  // gebuendelte Aufrufe). Eine aus der Closure gerechnete Zielposition waere
  // dann die des ERSTEN Schritts, und der Anker zoege alle weiteren zurueck.
  // Erkannt wird die Bewegung stattdessen am Zeiger selbst (siehe unten).
  const advanceToNext = useCallback(() => {
    // K5-Befund: im ungefilterten Kartenpfad blieb "Weiter" am Autoload-Deckel
    // aktiv und schob nur noch den UNSICHTBAREN Zeiger (Anzeige klemmt bei
    // 999, der Zeiger lief bis total-1 weiter) — jeder Klick ein Schritt
    // Rueckweg ohne jede Rueckmeldung. Am Deckel endet die Navigation jetzt
    // an der letzten GELADENEN Zeile; die Entsperr-CTA haengt an !canGoNext
    // und erscheint damit von selbst.
    setBuffer((prev) => ({
      ...prev,
      index: Math.min(maxNavigableIndex(total, products.length, navClamped), prev.index + 1),
    }));
  }, [total, products.length, navClamped]);

  const goToNext = advanceToNext;

  const goToPrev = useCallback(() => {
    setBuffer(stepBack);
  }, []);

  /**
   * Springt direkt auf ein Produkt — der Weg, den die Leiste braucht.
   *
   * Bis Task B4 konnte der Cursor nur schrittweise wandern (Tastatur, Pfeile),
   * also serialisiert. Ein Klick in der Leiste ist der erste Pfad, der ihn
   * BELIEBIG weit versetzt, und zwar moeglicherweise waehrend ein Textfeld den
   * Fokus haelt. Die Reihenfolge (speichern, abwarten, dann umsetzen) liegt
   * bewusst NICHT hier, sondern beim Aufrufer: nur der kennt den
   * Bearbeitungszustand. Diese Funktion versetzt, sonst nichts.
   *
   * Unbekannte ID heisst: nichts tun. Ein Sprung auf -1 wuerde die Karte
   * leeren, und das ist eine schlechtere Antwort als Stehenbleiben.
   */
  const goToProduct = useCallback(
    (id: string) => {
      const index = products.findIndex((p) => p.id === id);
      if (index < 0) return;
      // K4-Befund: die Ziel-ID IST die Identitaet — sie geht in den Anker,
      // und das Sprung-Flag laesst ihn im naechsten Render fuehren. Der
      // Index hier kann stale sein (Bewegung wird vom Tor geparkt, waehrend
      // eine Seite ankommt und der Score-Sort umsortiert); der Anker loest
      // die ID dann in der DANN aktuellen Liste auf.
      cursorAnchorRef.current = id;
      anchorJumpRef.current = true;
      setBuffer((prev) => (prev.index === index ? prev : { ...prev, index }));
    },
    [products],
  );

  const acceptProduct = useCallback(async (id: string) => {
    setReviewDecisionMap((prev) => {
      const next = new Map(prev);
      next.set(id, 'approved');
      return next;
    });
    advanceToNext();
    // Optimistisch fortschreiben statt neun COUNT-Abfragen anzustossen.
    // Der Serverabgleich passiert beim naechsten Routenwechsel oder Remount.
    // Der Upload-Zustand entscheidet, welche der vier Zahlen sich bewegen
    // duerfen (siehe applyDecisionToStats).
    const alreadyUploaded = uploadedLoadedIdsRef.current.has(id);
    queryClient.setQueryData(['workbench-pending-count', selectedFeedId], (old: number | undefined) =>
      typeof old === 'number' ? Math.max(0, old - 1) : old,
    );
    queryClient.setQueryData(['simple-product-stats', selectedFeedId], (old: ProductStats | undefined) =>
      old ? applyDecisionToStats(old, 'approved', alreadyUploaded) : old,
    );
    queryClient.setQueryData(['workbench-status-counts', selectedFeedId], (old: StatusCounts | undefined) =>
      moveStatusCount(old, 'pending', 'approved'),
    );
    // V2-Befund: die Produktlisten der ANDEREN Reiter wussten von der
    // Entscheidung nichts. Wer "Freigegeben" schon einmal geoeffnet hatte
    // (leer) und binnen der 30 Sekunden `staleTime` zurueckkam, bekam den
    // alten, leeren Zwischenspeicher ohne Nachladen — und damit den
    // Leerzustand "Noch nichts freigegeben" ueber einer Menge, die er selbst
    // gerade gefuellt hat.
    //
    // `refetchType: 'none'` ist der Kern: markiert wird nur als veraltet, ein
    // Nachladen loest es NICHT aus. Sonst wuerde die gerade betrachtete Liste
    // mitten in der Sichtung neu geholt und das eben entschiedene Produkt
    // verschwaende unter dem Cursor — genau das, was die optimistische
    // Fortschreibung hier vermeidet. Der Zielreiter laedt beim Wechsel selbst
    // nach, weil `refetchOnMount` auf veraltete Eintraege greift.
    queryClient.invalidateQueries({
      queryKey: ['workbench-products', selectedFeedId],
      refetchType: 'none',
    });
    queryClient.invalidateQueries({
      queryKey: ['workbench-server-total', selectedFeedId],
      refetchType: 'none',
    });

    const { error } = await supabase
      .from('products')
      .update({ approval_status: 'approved' })
      .eq('id', id);

    if (error) {
      setReviewDecisionMap((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      // K6-Befund: erst die LOKALE Kehrseite (braucht kein Netz), dann die
      // Invalidierung als Abgleich. Vorher hing der Rollback allein am
      // Refetch — und der teilte die Stoerung, die das UPDATE gerissen hat.
      queryClient.setQueryData(['workbench-pending-count', selectedFeedId], (old: number | undefined) =>
        typeof old === 'number' ? old + 1 : old,
      );
      queryClient.setQueryData(['simple-product-stats', selectedFeedId], (old: ProductStats | undefined) =>
        old ? revertDecisionFromStats(old, 'approved', alreadyUploaded) : old,
      );
      queryClient.setQueryData(['workbench-status-counts', selectedFeedId], (old: StatusCounts | undefined) =>
        moveStatusCount(old, 'approved', 'pending'),
      );
      queryClient.invalidateQueries({ queryKey: ['workbench-pending-count', selectedFeedId] });
      queryClient.invalidateQueries({ queryKey: ['simple-product-stats', selectedFeedId] });
      queryClient.invalidateQueries({ queryKey: ['workbench-status-counts', selectedFeedId] });
      toast({
        title: i18n.t('workbench:toasts.acceptFailed'),
        variant: 'destructive',
      });
    } else {
      // N7: die Freigabe-Entscheidung ist der Flaschenhals des gesamten
      // Beweisapparats und hatte bis hierher keine einzige Aufrufstelle.
      // Gemeldet wird erst nach dem erfolgreichen Schreiben, nicht beim Klick —
      // ein zurueckgerollter Optimismus ist keine Entscheidung.
      trackConversion('approval_decision', { decision: 'approved' });
      /*
       * Die Zahlen der Sammelfreigabe muessen mit: sie TREIBEN EINEN
       * SCHREIBVORGANG.
       *
       * `workbench_bulk_summary` zaehlt nur Zeilen auf `pending_review`. Wer
       * 5 von 12 Produkten einzeln freigibt, bekam ohne diese Zeile weiterhin
       * "Alle 12 freigeben" im Menue und dieselbe 12 in der Rueckfrage,
       * waehrend die RPC danach 7 schreibt. Die Erfolgsmeldung widersprach
       * damit der Rueckfrage, und die Rueckfrage ist genau die Stelle, an der
       * die Zahl eine Entscheidung traegt. Auf origin/main hielt
       * `filterUndecided(goodPendingIds, reviewDecisionMap)` das zusammen; die
       * client-seitige Kandidatenliste ist mit dem Serverpfad entfallen und hat
       * keinen Ersatz bekommen.
       *
       * Bewusst OHNE `refetchType: 'none'`, anders als die beiden
       * Invalidierungen im optimistischen Block oben: die Zusammenfassung
       * haengt dauerhaft am Hook, ein blosses Veralten loeste also nie ein
       * Nachladen aus und das Menue bliebe so lange falsch, wie der Nutzer auf
       * der Werkbank steht. Die Begruendung fuer 'none' greift hier ohnehin
       * nicht: gezaehlt wird feed-weit, die Liste unter dem Cursor ruehrt das
       * nicht an.
       *
       * Nach dem Schreiben und nicht davor: ein Nachladen VOR dem UPDATE
       * zaehlte den alten Stand und schriebe ihn frisch in den Cache.
       */
      queryClient.invalidateQueries({ queryKey: ['workbench-bulk-summary', selectedFeedId] });
      toast({ tone: 'success', title: i18n.t('workbench:toasts.accepted') });
    }
  }, [advanceToNext, selectedFeedId, queryClient, toast]);

  // 6.4: Einzel-Ablehnen laeuft bewusst ohne Bestaetigungsdialog (die
  // Leertaste ist der schnellste Weg durch den Stapel) — dann muss die
  // Entscheidung vorfuehrbar zuruecknehmbar sein: 'rejected' faellt aus allen
  // Werkbank-Abfragen, es gibt keine Ansicht abgelehnter Produkte, und der
  // einzige andere Rueckweg ist eine gedeckelte Re-Optimierung
  // (MAX_REOPTIMIZATIONS=2 pro Feed-Lifetime plus Kontingent). Der Undo im
  // Ablehnen-Toast schreibt ueber denselben Einzel-Update-Pfad auf
  // 'pending_review' zurueck — kein neues Server-API.
  //
  // Der Ref sperrt Doppel-Feuer (2.6) pro Produkt; rejectProduct raeumt den
  // Eintrag wieder ab, damit ein ERNEUTES Ablehnen nach einem Undo wieder
  // zuruecknehmbar ist.
  const undoneRejectsRef = useRef<Set<string>>(new Set());

  // K7-Befund: alreadyUploaded wurde zum UNDO-Zeitpunkt neu aus dem Ref
  // gelesen — der spiegelt die AKTUELL geladene Liste. Nach einem Feed-/
  // Reiterwechsel im 8s-Toast-Fenster kannte er die Zeile nicht mehr und
  // lieferte false: fuer eine hochgeladene Zeile erfand die Kehrseite damit
  // eine Wartemenge, die es nie gab. Der Wert kommt jetzt vom zugehoerigen
  // Reject (dort korrekt eingefangen); der Ref bleibt nur als Rueckfall fuer
  // Aufrufer ohne Kontext.
  const undoReject = useCallback(async (id: string, alreadyUploadedAtReject?: boolean) => {
    if (undoneRejectsRef.current.has(id)) return;
    undoneRejectsRef.current.add(id);

    // Write-first statt optimistisch: der Ausloeser liegt in einem Toast,
    // ein Fehlschlag darf die Anzeige nicht erst umklappen und dann
    // zuruecknehmen muessen.
    // K6-Befund: ohne das Praedikat nahm der Toast-Rueckweg eine Ablehnung
    // auch dann "zurueck", wenn sie laengst ueber den Abgelehnt-Reiter
    // zurueckgenommen war (reopenProduct) — dieselbe Ablehnung wurde ZWEIMAL
    // aus den Zaehlern herausgerechnet. Das Praedikat macht das UPDATE
    // idempotent; trifft es keine Zeile, gibt es nichts zu verschieben.
    const { data: updatedRows, error } = await supabase
      .from('products')
      .update({ approval_status: 'pending_review' })
      .eq('id', id)
      .eq('approval_status', 'rejected')
      .select('id');

    if (error) {
      // Freigabe, damit ein erneuter Versuch moeglich bleibt.
      undoneRejectsRef.current.delete(id);
      // "Bitte versuche es erneut" braucht einen Weg, es zu tun: der
      // Original-Toast mit dem Rueckgaengig-Knopf ist beim Action-Klick
      // bereits geschlossen (sonner), die Karte bleibt gesperrt und
      // 'rejected' faellt aus allen Werkbank-Abfragen. Ohne Action hier
      // endet der 6.4-Rueckweg in einer Sackgasse. Gleiches 8s-Fenster wie
      // der Original-Toast.
      toast({
        title: i18n.t('workbench:toasts.undoRejectFailed'),
        variant: 'destructive',
        action: {
          label: i18n.t('workbench:toasts.undoRetryAction'),
          onClick: () => { void undoReject(id, alreadyUploadedAtReject); },
        },
        durationMs: 8000,
      });
      return;
    }

    if ((updatedRows?.length ?? 0) === 0) {
      // Zeile war nicht mehr 'rejected' — nichts wurde geschrieben, also
      // wird auch nichts verschoben. Nur die Sicht heilen (die Zeile kann
      // noch mit alter Pille im Cache stehen); der Ref-Eintrag bleibt,
      // ein zweiter Klick haette ebenso nichts zu tun.
      queryClient.invalidateQueries({ queryKey: ['workbench-products', selectedFeedId] });
      queryClient.invalidateQueries({ queryKey: ['workbench-server-total', selectedFeedId] });
      return;
    }

    // Karte/Zeile ist wieder unentschieden: Knoepfe entsperren sich ueber
    // isCurrentReviewed, die Sitzungszaehler und undecidedLoadedCount rechnen
    // aus derselben Map. Der Puffer selbst bleibt unangetastet.
    setReviewDecisionMap((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    queryClient.setQueryData(['workbench-pending-count', selectedFeedId], (old: number | undefined) =>
      typeof old === 'number' ? old + 1 : old,
    );
    // Dieselbe Auskunft wie beim Ablehnen: die Zeile bleibt im Puffer stehen,
    // die Ref kennt sie also noch. Ohne dasselbe Flag waere dies keine
    // Kehrseite, sondern ein Inkrement auf eine Menge, die die Ablehnung nie
    // gesenkt hat.
    const alreadyUploaded = alreadyUploadedAtReject ?? uploadedLoadedIdsRef.current.has(id);
    queryClient.setQueryData(['simple-product-stats', selectedFeedId], (old: ProductStats | undefined) =>
      old ? revertRejectFromStats(old, alreadyUploaded) : old,
    );
    // Kehrseite des Schubs in `rejectProduct`. Write-first-Pfad, deshalb erst
    // hier — vor dem erfolgreichen Schreiben gibt es nichts zurueckzuschieben.
    queryClient.setQueryData(['workbench-status-counts', selectedFeedId], (old: StatusCounts | undefined) =>
      moveStatusCount(old, 'rejected', 'pending'),
    );
    // K1-Befund: ohne diese Invalidierung blieb die zurueckgenommene Zeile im
    // Reiter "Abgelehnt" mit Pille 'Abgelehnt' stehen, waehrend die Reiterzahl
    // schon gesunken war — Kopf und Liste widersprachen sich auf demselben
    // Bildschirm, ohne Heilung. Standard-Refetch (nicht 'none'): der Nutzer
    // kann in diesem Moment auf dem Reiter stehen und die Zeile ansehen.
    queryClient.invalidateQueries({ queryKey: ['workbench-products', selectedFeedId] });
    queryClient.invalidateQueries({ queryKey: ['workbench-server-total', selectedFeedId] });
    // Gegenrichtung zu `rejectProduct`: die Zeile steht wieder auf
    // `pending_review` und zaehlt damit wieder in jede Zahl der
    // Sammelfreigabe. Nur hier, nicht im Zweig ohne getroffene Zeile oben:
    // dort wurde nichts geschrieben, also hat sich auch nichts zu zaehlen.
    queryClient.invalidateQueries({ queryKey: ['workbench-bulk-summary', selectedFeedId] });
    toast({ tone: 'success', title: i18n.t('workbench:toasts.undoRejectDone') });
  }, [selectedFeedId, queryClient, toast]);

  /**
   * Zurueck in die Pruefung — der Rueckweg aus den Reitern "Freigegeben" und
   * "Abgelehnt" (Task A7).
   *
   * Bewusst NICHT `undoReject` wiederverwendet, obwohl beide dieselbe Spalte
   * auf denselben Wert schreiben. `undoReject` gehoert dem Toast-Rueckweg
   * direkt nach einer Ablehnung: es traegt einen Einmal-Guard
   * (`undoneRejectsRef`) und rechnet die Kachelzahlen mit
   * `revertRejectFromStats` zurueck — eine Rechnung, die voraussetzt, dass der
   * vorherige Zustand `rejected` war. Auf eine zuruckgenommene FREIGABE
   * angewandt zaehlte sie in die falsche Richtung.
   *
   * Hier wird deshalb nicht optimistisch gerechnet, sondern invalidiert. Das
   * ist eine Netzwerkrunde langsamer und dafuer richtig, ohne dass eine
   * zweite Zahlen-Herleitung gepflegt werden muss. Das Produkt verlaesst mit
   * dem Schreibvorgang ohnehin den aktiven Reiter, die Liste muss also so
   * oder so nachladen.
   */
  const reopenProduct = useCallback(async (id: string) => {
    const { error } = await supabase
      .from('products')
      .update({ approval_status: 'pending_review' })
      .eq('id', id);

    if (error) {
      toast({
        title: i18n.t('workbench:toasts.reopenFailed'),
        variant: 'destructive',
      });
      return false;
    }

    setReviewDecisionMap((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    resetBuffer();
    queryClient.invalidateQueries({ queryKey: ['workbench-status-counts', selectedFeedId] });
    queryClient.invalidateQueries({ queryKey: ['workbench-pending-count', selectedFeedId] });
    queryClient.invalidateQueries({ queryKey: ['workbench-server-total', selectedFeedId] });
    queryClient.invalidateQueries({ queryKey: ['workbench-products', selectedFeedId] });
    queryClient.invalidateQueries({ queryKey: ['simple-product-stats', selectedFeedId] });
    // Dieselbe Spalte auf denselben Wert wie `undoReject`, also dieselbe
    // Wirkung auf die Zahlen der Sammelfreigabe: das Produkt zaehlt wieder mit.
    queryClient.invalidateQueries({ queryKey: ['workbench-bulk-summary', selectedFeedId] });
    toast({ tone: 'success', title: i18n.t('workbench:toasts.reopenDone') });
    return true;
  }, [selectedFeedId, queryClient, toast, resetBuffer]);

  const rejectProduct = useCallback(async (id: string) => {
    setReviewDecisionMap((prev) => {
      const next = new Map(prev);
      next.set(id, 'rejected');
      return next;
    });
    advanceToNext();
    // Optimistisch fortschreiben statt neun COUNT-Abfragen anzustossen.
    // Der Serverabgleich passiert beim naechsten Routenwechsel oder Remount.
    const alreadyUploaded = uploadedLoadedIdsRef.current.has(id);
    queryClient.setQueryData(['workbench-pending-count', selectedFeedId], (old: number | undefined) =>
      typeof old === 'number' ? Math.max(0, old - 1) : old,
    );
    queryClient.setQueryData(['simple-product-stats', selectedFeedId], (old: ProductStats | undefined) =>
      old ? applyDecisionToStats(old, 'rejected', alreadyUploaded) : old,
    );
    queryClient.setQueryData(['workbench-status-counts', selectedFeedId], (old: StatusCounts | undefined) =>
      moveStatusCount(old, 'pending', 'rejected'),
    );
    // Siehe `acceptProduct`: nur als veraltet markieren, nicht nachladen.
    queryClient.invalidateQueries({
      queryKey: ['workbench-products', selectedFeedId],
      refetchType: 'none',
    });
    queryClient.invalidateQueries({
      queryKey: ['workbench-server-total', selectedFeedId],
      refetchType: 'none',
    });

    const { error } = await supabase
      .from('products')
      .update({ approval_status: 'rejected' })
      .eq('id', id);

    if (error) {
      setReviewDecisionMap((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      // K6-Befund: lokale Kehrseite vor der Invalidierung (siehe accept).
      queryClient.setQueryData(['workbench-pending-count', selectedFeedId], (old: number | undefined) =>
        typeof old === 'number' ? old + 1 : old,
      );
      queryClient.setQueryData(['simple-product-stats', selectedFeedId], (old: ProductStats | undefined) =>
        old ? revertDecisionFromStats(old, 'rejected', alreadyUploaded) : old,
      );
      queryClient.setQueryData(['workbench-status-counts', selectedFeedId], (old: StatusCounts | undefined) =>
        moveStatusCount(old, 'rejected', 'pending'),
      );
      queryClient.invalidateQueries({ queryKey: ['workbench-pending-count', selectedFeedId] });
      queryClient.invalidateQueries({ queryKey: ['simple-product-stats', selectedFeedId] });
      queryClient.invalidateQueries({ queryKey: ['workbench-status-counts', selectedFeedId] });
      toast({
        title: i18n.t('workbench:toasts.rejectFailed'),
        variant: 'destructive',
      });
    } else {
      trackConversion('approval_decision', { decision: 'rejected' });
      // Gleiche Begruendung wie in `acceptProduct`: die abgelehnte Zeile faellt
      // aus `pending_review` und damit aus jeder Zahl der Sammelfreigabe. Ohne
      // das naehme die Rueckfrage eine Menge an, die es nicht mehr gibt.
      queryClient.invalidateQueries({ queryKey: ['workbench-bulk-summary', selectedFeedId] });
      // Ein frisches Reject macht einen frueheren Undo-Eintrag ungueltig.
      undoneRejectsRef.current.delete(id);
      toast({
        tone: 'success',
        title: i18n.t('workbench:toasts.rejected'),
        action: {
          label: i18n.t('workbench:toasts.undoAction'),
          onClick: () => { void undoReject(id, alreadyUploaded); },
        },
        // Laengeres Fenster als die Sonner-Default (~4s): der Toast IST der
        // Rueckweg der einzigen unbestaetigten destruktiven Aktion.
        durationMs: 8000,
      });
    }
  }, [advanceToNext, selectedFeedId, queryClient, toast, undoReject]);

  const bulkApprove = useCallback(async (ids: string[]) => {
    if (!selectedFeedId || ids.length === 0) return 0;

    try {
      const { data, error } = await supabase.rpc('bulk_update_approval_status', {
        p_product_ids: ids,
        p_approval_status: 'approved',
        p_feed_id: selectedFeedId,
      });

      if (error) throw error;

      // Die Map folgt der RPC-Semantik (Konvergenz-5): bulk_update_approval_status
      // trifft nur pending_review-Zeilen. Bereits getroffene Sitzungs-
      // Entscheidungen (insbesondere Einzel-Ablehnungen) bleiben stehen —
      // vorher wurden sie hier stillschweigend als 'approved' ueberschrieben,
      // waehrend die DB 'rejected' behielt.
      setReviewDecisionMap((prev) => applyBulkDecision(prev, ids, 'approved'));

      // Invalidate queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['workbench-pending-count', selectedFeedId] });
      queryClient.invalidateQueries({ queryKey: ['workbench-server-total', selectedFeedId] });
      // Ein 2-Element-Praefix trifft alle Suchvarianten dieses Feeds, die in
      // dieser Sitzung schon gefetcht wurden. Eine Freigabe nimmt Zeilen aus
      // der offenen Menge, also stimmen danach alle fuenf Zahlen der
      // Sammelfreigabe nicht mehr.
      queryClient.invalidateQueries({ queryKey: ['workbench-bulk-summary', selectedFeedId] });
      // V2-Befund: der Sammelpfad zog die Reiterzahlen nicht nach. Hier
      // invalidieren statt fortschreiben — die RPC entscheidet selbst, wie
      // viele Zeilen sie trifft (sie filtert auf pending_review), und eine
      // Sammelhandlung ist selten genug, dass drei COUNTs nicht ins Gewicht
      // fallen. Fortschreiben mit der uebergebenen Menge waere hier sogar
      // falsch: `ids` kann Zeilen enthalten, die die RPC nicht anfasst.
      queryClient.invalidateQueries({ queryKey: ['workbench-status-counts', selectedFeedId] });
      queryClient.invalidateQueries({ queryKey: ['simple-product-stats'] });
      // K1-Befund: der #160-Fix deckte nur die Einzelpfade — hier stand der
      // EXAKTE Key (aktueller Reiter + Suche), die Listen der anderen Reiter
      // blieben 30 s stale ("Reiterzahl 63, Liste 13 Zeilen"). Feed-Praefix
      // wie in reopenProduct; der Standard-Refetch ist hier richtig, weil
      // resetBuffer ohnehin laeuft.
      queryClient.invalidateQueries({ queryKey: ['workbench-products', selectedFeedId] });
      // Reine Invalidierung fuellt den Puffer nicht nach (er wird nur als
      // Seiteneffekt von queryFn befuellt, siehe oben) — ein Bulk-Approve kann
      // Produkte betreffen, die schon im Puffer stehen und dort faelschlich
      // weiter als "pending" erscheinen wuerden. Ein voller Reset ist hier
      // korrekt: bereits entschiedene Produkte sind serverseitig nicht mehr
      // pending_review und tauchen beim Neuanlauf nicht wieder auf (F4).
      // resetBuffer() statt setBuffer(emptyBuffer(...)) direkt: erhoeht die
      // Generation, auch wenn die Population (Feed+Suche) unveraendert bleibt
      // — sonst wuerde ein waehrend des Bulk-Approve noch laufender Prefetch
      // fuer dieselbe Population den Identity-Guard in appendPage passieren
      // (Review-Fund F3, Punkt 2).
      resetBuffer();

      const count = data as number;
      toast({
        tone: 'success',
        title: i18n.t('workbench:toasts.bulkApproved', { count }),
        // H4-F5 (E2E-Sweep 25.08.): das Ergebnis einer Sammel-Entscheidung ist
        // erst zu sehen, nachdem die Liste nachgeladen hat — die Sonner-Default
        // (~4 s) war dafuer zu kurz. Gleiche Standzeit wie der Zwilling
        // `release.doneToast` in WorkbenchReviewer (Spec 9.5).
        durationMs: 12000,
      });

      return count;
    } catch (error) {
      toast({
        title: i18n.t('workbench:toasts.bulkApproveFailed'),
        variant: 'destructive',
      });
      console.error('Bulk approve failed:', error);
      return 0;
    }
  }, [selectedFeedId, queryClient, toast, resetBuffer]);

  const bulkReject = useCallback(async (ids: string[]) => {
    if (!selectedFeedId || ids.length === 0) return 0;

    try {
      const { data, error } = await supabase.rpc('bulk_update_approval_status', {
        p_product_ids: ids,
        p_approval_status: 'rejected',
        p_feed_id: selectedFeedId,
      });

      if (error) throw error;

      // Symmetrisch zu bulkApprove (Konvergenz-5): die RPC trifft nur
      // pending_review-Zeilen, eine Sitzungs-Freigabe wird hier nicht
      // stillschweigend zur Ablehnung.
      setReviewDecisionMap((prev) => applyBulkDecision(prev, ids, 'rejected'));

      // Invalidate queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['workbench-pending-count', selectedFeedId] });
      queryClient.invalidateQueries({ queryKey: ['workbench-server-total', selectedFeedId] });
      // Ein 2-Element-Praefix trifft alle Suchvarianten dieses Feeds, die in
      // dieser Sitzung schon gefetcht wurden.
      // Eine Ablehnung nimmt Zeilen aus der offenen Menge (die RPC trifft nur
      // noch pending_review). Ohne diese Invalidierung stand am Menueeintrag
      // der Sammelfreigabe weiter die alte Zahl, und die Rueckfrage versprach
      // einen Umfang, den der naechste Klick mit "0 Produkte freigegeben"
      // widerlegte - bulkApprove invalidiert die Zahlen laengst, bulkReject
      // hatte die Zeile schlicht nicht.
      queryClient.invalidateQueries({ queryKey: ['workbench-bulk-summary', selectedFeedId] });
      // V2-Befund: der Sammelpfad zog die Reiterzahlen nicht nach. Hier
      // invalidieren statt fortschreiben — die RPC entscheidet selbst, wie
      // viele Zeilen sie trifft (sie filtert auf pending_review), und eine
      // Sammelhandlung ist selten genug, dass drei COUNTs nicht ins Gewicht
      // fallen. Fortschreiben mit der uebergebenen Menge waere hier sogar
      // falsch: `ids` kann Zeilen enthalten, die die RPC nicht anfasst.
      queryClient.invalidateQueries({ queryKey: ['workbench-status-counts', selectedFeedId] });
      queryClient.invalidateQueries({ queryKey: ['simple-product-stats'] });
      // K1-Befund: der #160-Fix deckte nur die Einzelpfade — hier stand der
      // EXAKTE Key (aktueller Reiter + Suche), die Listen der anderen Reiter
      // blieben 30 s stale ("Reiterzahl 63, Liste 13 Zeilen"). Feed-Praefix
      // wie in reopenProduct; der Standard-Refetch ist hier richtig, weil
      // resetBuffer ohnehin laeuft.
      queryClient.invalidateQueries({ queryKey: ['workbench-products', selectedFeedId] });
      // Siehe bulkApprove: reine Invalidierung fuellt den Puffer nicht nach,
      // ein voller Reset ist hier korrekt (F4); resetBuffer() erhoeht die
      // Generation, damit ein noch laufender Prefetch fuer dieselbe Population
      // den Identity-Guard nicht passiert (F3, Punkt 2).
      resetBuffer();

      const count = data as number;
      toast({
        tone: 'success',
        title: i18n.t('workbench:toasts.bulkRejected', { count }),
      });

      return count;
    } catch (error) {
      toast({
        title: i18n.t('workbench:toasts.bulkRejectFailed'),
        variant: 'destructive',
      });
      console.error('Bulk reject failed:', error);
      return 0;
    }
  }, [selectedFeedId, queryClient, toast, resetBuffer]);

  /**
   * Invalidierungssatz der Sammelhandlungen (Etappe 4).
   *
   * Wortgleich zu dem, was `bulkApprove`/`bulkReject` schon einzeln auffuehren,
   * plus die Zahlen der Sammelfreigabe selbst. Ausgelagert, weil `approveScope`
   * und `reopenBulk` denselben Satz brauchen: drei Kopien derselben Liste
   * driften auseinander, und der Fehler zeigt sich dann als "Zahl stimmt nach
   * dem Rueckgaengig nicht mehr", nicht als roter Test.
   */
  const invalidateAfterBulkWrite = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['workbench-pending-count', selectedFeedId] });
    queryClient.invalidateQueries({ queryKey: ['workbench-server-total', selectedFeedId] });
    queryClient.invalidateQueries({ queryKey: ['workbench-bulk-summary', selectedFeedId] });
    queryClient.invalidateQueries({ queryKey: ['workbench-status-counts', selectedFeedId] });
    queryClient.invalidateQueries({ queryKey: ['simple-product-stats'] });
    queryClient.invalidateQueries({ queryKey: ['workbench-products', selectedFeedId] });
  }, [queryClient, selectedFeedId]);

  /**
   * Sammelfreigabe ueber eine serverseitig bestimmte Menge (Spec 9.2/9.4).
   *
   * Anders als `bulkApprove` bekommt diese Funktion KEINE ID-Liste: die RPC
   * waehlt selbst aus, was sie schreibt, und meldet zurueck, was sie getroffen
   * hat. Damit kann die im Menue angezeigte Zahl nicht gegen die geschriebene
   * driften, und es wandert keine Liste mit zehntausenden UUIDs ueber den
   * Draht.
   *
   * WARUM IN SCHLEIFE, und warum die Schleife hier steht und nicht in der
   * Funktion: gemessen kostet das Schreiben 0,086 ms je Zeile, das
   * statement_timeout der Rolle `authenticated` ist 8 s. Bei 61.022 offenen
   * Zeilen (groesster Feed im Bestand) waeren das rund 5,2 s allein fuer den
   * Schreibteil, dazu der Lesedurchgang - ueber dem Budget. Eine Schleife
   * INNERHALB der Funktion hilft nicht: das statement_timeout gilt fuer das
   * Top-Level-Statement, nicht fuer die einzelne Anweisung darin. Es muessen
   * also mehrere Roundtrips sein, jeder committet seinen Fortschritt.
   * Hausmuster: `delete_mc_products_batch` (Sentry DENO-4).
   *
   * Der Deckel von 25 Durchlaeufen ist reine Notbremse, kein erwarteter Fall:
   * 25 * 10.000 = 250.000 Zeilen liegt weit ueber jedem realen Feed. Er
   * schuetzt gegen eine Endlosschleife, falls die RPC jemals eine Menge
   * meldet, die sie nicht schrumpfen laesst.
   *
   * Wirft NIE - Spiegel von `bulkApprove`. Bei einem Fehler im dritten
   * Durchlauf sind die ersten beiden bereits geschrieben; die Rueckgabe nennt
   * genau diese Menge, damit der Aufrufer auch fuer einen Teil-Erfolg
   * "Rueckgaengig" anbieten kann. Der Erfolgs-Toast gehoert bewusst NICHT
   * hierher: er traegt die Rueckgaengig-Handlung und damit den Aufrufer.
   */
  const approveScope = useCallback(async (
    scope: BulkScope,
  ): Promise<{ approved: number; ids: string[] }> => {
    if (!selectedFeedId) return { approved: 0, ids: [] };

    const ids: string[] = [];
    let approved = 0;

    try {
      for (let durchlauf = 0; durchlauf < BULK_APPROVE_MAX_ROUNDS; durchlauf++) {
        const params = {
          p_feed_id: selectedFeedId,
          p_scope: scope,
          p_search: filters.search || null,
          p_limit: BULK_WRITE_CHUNK,
        };
        const { data, error } = await supabase.rpc('workbench_bulk_approve', params);
        if (error) throw error;

        const row = data?.[0];
        const geschrieben = Number(row?.approved_count ?? 0);
        if (row?.approved_ids) ids.push(...row.approved_ids);
        approved += geschrieben;

        // 0 heisst "nichts mehr uebrig" - das ist die Abbruchbedingung, nicht
        // die volle Chunk-Groesse. Ein Durchlauf kann weniger als p_limit
        // schreiben, obwohl noch etwas offen ist: die RPC waehlt bis p_limit
        // Kandidaten aus, schreibt aber nur die, die im selben Moment noch auf
        // pending_review stehen (paralleler Einzel-Klick, zweiter Tab).
        if (geschrieben === 0) break;
      }
    } catch (error) {
      toast({
        title: i18n.t('workbench:toasts.bulkApproveFailed'),
        variant: 'destructive',
      });
      console.error('Bulk approve (scope) failed:', error);
    }

    if (approved > 0) {
      // Wie `bulkApprove`: die Map folgt der RPC-Semantik. Bereits getroffene
      // Sitzungs-Entscheidungen bleiben stehen. Hier sogar zwingend, denn die
      // RPC liefert nur die Zeilen zurueck, die sie WIRKLICH geschrieben hat.
      setReviewDecisionMap((prev) => applyBulkDecision(prev, ids, 'approved'));
      invalidateAfterBulkWrite();
      // resetBuffer() statt setBuffer(emptyBuffer(...)): erhoeht die
      // Generation, damit ein noch laufender Prefetch fuer dieselbe Population
      // den Identity-Guard in appendPage nicht passiert (F3, Punkt 2).
      resetBuffer();
    }

    return { approved, ids };
  }, [selectedFeedId, filters.search, toast, invalidateAfterBulkWrite, resetBuffer]);

  /**
   * Rueckweg der Sammelfreigabe (Spec 9.5): setzt genau die freigegebenen
   * Zeilen wieder auf `pending_review`.
   *
   * Eigene RPC und nicht `bulk_update_approval_status`, aus zwei Gruenden, die
   * beide in der Migration stehen: deren Wert-Guard laesst nur
   * 'approved'/'rejected' zu, und ihr Zeilenfilter trifft nur
   * `pending_review` - der Rueckweg ist damit doppelt gesperrt.
   *
   * Gechunkt aus demselben Grund wie `approveScope`, hier zusaetzlich wegen
   * der Nutzlast: PostgREST schickt `uuid[]` im POST-Body, aber 23.401 IDs
   * sind auch dort rund 0,9 MB. 10.000 je Aufruf halten Nutzlast und
   * Schreibzeit (0,086 ms/Zeile, also ~0,9 s) klar unter dem 8-s-Budget.
   *
   * Wirft NIE, gleiche Begruendung wie oben: was schon zurueckgesetzt ist,
   * bleibt zurueckgesetzt und wird auch so gemeldet.
   *
   * Der Aufrufer ist eine Meldung mit 12 Sekunden Standzeit, der Feed kann
   * also zwischen Freigabe und Klick gewechselt haben. Das SCHREIBEN bleibt
   * dann richtig (IDs und `p_feed_id` haengen an dieser Closure), die
   * anschliessende Aufraeumarbeit nicht: sie wuerde den Puffer des INZWISCHEN
   * montierten Feeds leeren. Deshalb der Abgleich gegen `feedIdRef`.
   */
  const reopenBulk = useCallback(async (ids: string[]): Promise<BulkReopenResult> => {
    if (!selectedFeedId || ids.length === 0) return { zurueck: 0, vollstaendig: true };

    let reopened = 0;
    let vollstaendig = true;
    const behandelt: string[] = [];

    try {
      for (let offset = 0; offset < ids.length; offset += BULK_WRITE_CHUNK) {
        const chunk = ids.slice(offset, offset + BULK_WRITE_CHUNK);
        const params = { p_feed_id: selectedFeedId, p_product_ids: chunk };
        const { data, error } = await supabase.rpc('workbench_bulk_reopen', params);
        if (error) throw error;

        reopened += Number(data ?? 0);
        behandelt.push(...chunk);
      }
    } catch (error) {
      // KEINE Meldung von hier. Ein Teilausfall (Block 2 von 3 scheitert)
      // liefert sonst gleichzeitig diese Fehlermeldung UND beim Aufrufer die
      // Erfolgsmeldung, weil `reopened` dann eine echte Teilsumme traegt.
      // Der Aufrufer kennt beide Zahlen und sagt EINEN Satz.
      vollstaendig = false;
      console.error('Bulk reopen failed:', error);
    }

    // Steht der Hook noch auf dem Feed, zu dem dieser Rueckweg gehoert?
    // `feedIdRef` traegt den AKTUELLEN Stand, `selectedFeedId` in dieser
    // Closure den von der Freigabe. Weichen sie ab, hat der Nutzer waehrend der
    // 12 Sekunden den Feed gewechselt: `resetBuffer` haengt an `setBuffer` des
    // montierten Hooks und wuerfe dem NEUEN Feed seine Cursorposition weg,
    // waehrend `invalidateAfterBulkWrite` unter anderem `simple-product-stats`
    // ohne Feed-Bindung invalidiert und damit ebenfalls in die fremde Ansicht
    // greift. Der Schreibvorgang oben ist davon unberuehrt und bleibt richtig.
    const gleicherFeed = feedIdRef.current === selectedFeedId;

    if (behandelt.length > 0 && gleicherFeed) {
      // LOESCHEN, nicht auf 'pending_review' setzen: der Leerzustand
      // "alles geprueft" liest die blosse Anwesenheit eines Schluessels als
      // getroffene Entscheidung (siehe removeBulkDecisions).
      //
      // Ausgeraeumt wird nach der behandelten MENGE, nicht nach der
      // Rueckgabezahl: die RPC nennt nur die Anzahl, nicht welche Zeilen sie
      // getroffen hat. Zeilen, die sie nicht anfasst, sind ohnehin nicht mehr
      // 'approved' - fuer die ist der Eintrag in der Sitzungs-Map genauso
      // falsch.
      //
      // Auch die Sitzungs-Map steht unter dem Feed-Vorbehalt: nach einem
      // Feedwechsel gehoert sie dem neuen Feed (der Reset laeuft ueber
      // `decisionScope`), und die IDs von hier haben dort nichts zu suchen.
      setReviewDecisionMap((prev) => removeBulkDecisions(prev, behandelt));
      invalidateAfterBulkWrite();
      resetBuffer();
    }

    return { zurueck: reopened, vollstaendig };
  }, [selectedFeedId, invalidateAfterBulkWrite, resetBuffer]);

  const saveChanges = useCallback(async (
    id: string,
    edits: Partial<WorkbenchEditState>,
  ) => {
    const { error } = await supabase
      .from('products')
      .update({
        ...(edits.optimized_title !== undefined && { optimized_title: edits.optimized_title }),
        ...(edits.optimized_description !== undefined && { optimized_description: edits.optimized_description }),
        ...(edits.optimized_product_highlights !== undefined && { optimized_product_highlights: edits.optimized_product_highlights }),
        ...(edits.optimized_color !== undefined && { optimized_color: edits.optimized_color }),
        ...(edits.optimized_material !== undefined && { optimized_material: edits.optimized_material }),
        ...(edits.optimized_gender !== undefined && { optimized_gender: edits.optimized_gender }),
        ...(edits.optimized_age_group !== undefined && { optimized_age_group: edits.optimized_age_group }),
        ...(edits.optimized_size !== undefined && { optimized_size: edits.optimized_size }),
        ...(edits.optimized_pattern !== undefined && { optimized_pattern: edits.optimized_pattern }),
        ...(edits.optimized_google_product_category !== undefined && { optimized_google_product_category: edits.optimized_google_product_category }),
      })
      .eq('id', id);

    if (error) {
      toast({
        title: i18n.t('workbench:toasts.saveFailed'),
        variant: 'destructive',
      });
      return false;
    }

    // Kartenmodus: buffer.items ist die Anzeige-Quelle, nicht der Query-Cache
    // (siehe patchBufferItem-Doku) — ein Cache-Write auf den Karten-Seiten-Key
    // waere toter Code, den niemand zurueckliest. Listenmodus / score-
    // gefilterter Kartenmodus: fullProducts kommt aus dem Infinite-Query-
    // Cache (Task 6, P0-4) — jede Seite einzeln patchen, damit die
    // pages/pageParams-Struktur erhalten bleibt.
    // V2-Befund: gepatcht wurde nur der EINE gerade aktive Schluessel — und der
    // traegt Reiter und Suchbegriff. Der Auto-Save laeuft aber in dem Render,
    // in dem beide schon gewechselt HABEN: der Patch landete im Cache des
    // Reiters, in den der Nutzer gerade gewechselt ist, waehrend das
    // bearbeitete Produkt im Cache des VERLASSENEN lag. Beim Zurueckwechseln
    // innerhalb der 30 Sekunden `staleTime` serviert React Query die alten
    // Seiten ohne Refetch — die Korrektur war vom Bildschirm verschwunden,
    // waehrend die Karte weiter "bearbeitet" anzeigte (`editedSet` ueberlebt
    // den Reiterwechsel).
    //
    // `setQueriesData` mit dem PRAEFIX statt `setQueryData` mit dem exakten
    // Schluessel: dasselbe Produkt kann in mehreren Reitern und Suchergebnissen
    // desselben Feeds liegen, und nach einer Korrektur ist in JEDEM davon der
    // alte Text falsch. Andere Feeds bleiben unberuehrt — der Praefix endet bei
    // der Feed-ID.
    // K1-Befund: der Feed-Praefix hielt fuer die FEED-Dimension nicht. Der
    // Auto-Save beim Feedwechsel laeuft im Render NACH dem Wechsel — mit dem
    // selectedFeedId des NEUEN Feeds patchte er einen Cache, in dem die
    // Produkt-ID gar nicht vorkommt, waehrend der alte Feed seinen stalen
    // Eintrag behielt. Der Praefix endet deshalb VOR der Feed-ID: die
    // Produkt-IDs sind global eindeutige UUIDs, patchBufferItem trifft die
    // Zeile in dem Cache, in dem sie wirklich liegt, und laesst alle anderen
    // Seiten unveraendert.
    // K2-Befund: setQueriesData stempelte JEDEN befuellten Cache als frisch
    // (setQueryData dispatcht bedingungslos 'success': isInvalidated=false,
    // dataUpdatedAt=now) — der Auto-Save loeschte damit die Veraltet-Marken
    // des Entscheidungs-Fixes (Bekannt Nr. 30), und der feed-agnostische
    // K1-Praefix weitete das von einem Feed auf alle aus. Deshalb von Hand:
    // nur Caches anfassen, die die Zeile WIRKLICH enthalten, und eine
    // vorhandene Veraltet-Marke danach wieder setzen (refetchType 'none',
    // exakt wie sie gesetzt wurde).
    // K6-Befund: ein gleichzeitig laufender Refetch (Invalidierungs-Sturm von
    // aussen, z.B. Upload-Abschluss) committet sein GESAMTergebnis erst am
    // Ende — landete es nach dem Hand-Patch, ueberschrieb der alte Serverstand
    // die eben gespeicherte Korrektur in der Anzeige. Laufende Fetches werden
    // deshalb abgebrochen (Standard-Muster fuer manuelle Cache-Schreiber).
    //
    // K7-Befund: der Cancel war praefixweit und traf damit auch die
    // ERST-LADE eines soeben betretenen Reiters/Feeds (der Tipp-Nachzuegler-
    // Auto-Save feuert genau in diesem Fenster) — eine gecancelte Query OHNE
    // Daten revertiert auf pending/idle: kein Fehler, kein Retry, die
    // Werkbank blieb unter "Produkt 1 von N" dauerhaft leer. Gecancelt wird
    // deshalb nur noch EXAKT dort, wo auch gepatcht wird: in Caches, die die
    // Zeile wirklich enthalten. Eine Erstlade enthaelt nie Daten und wird
    // damit nie angefasst.
    const workbenchCaches = queryClient.getQueriesData<InfiniteData<WorkbenchProduct[]>>({
      queryKey: ['workbench-products'],
    });
    for (const [cacheKey, cacheData] of workbenchCaches) {
      if (!cacheData || !cacheData.pages.some((page) => page.some((p) => p.id === id))) continue;
      // K8-Befund: der Cancel toetete auch ABSICHTLICH gestartete aktive
      // Refetches (Upload-Abschluss, Bulk, Undo, Reopen, Sweep) — und die
      // Re-Marke mit 'none' startete sie nie neu: die Nr.-31/58/59-Bilder
      // kehrten ohne Heilung zurueck. Lief die Query gerade, wird sie nach
      // dem Patch mit 'active' neu angestossen; der neue Refetch laeuft NACH
      // dem DB-Write und kann den Hand-Patch nicht mehr ueberschreiben (K6).
      const zustandVorher = queryClient.getQueryState(cacheKey);
      const fetchteAktiv = zustandVorher?.fetchStatus === 'fetching';
      await queryClient.cancelQueries({ queryKey: cacheKey, exact: true });
      const warVeraltet = queryClient.getQueryState(cacheKey)?.isInvalidated ?? false;
      queryClient.setQueryData<InfiniteData<WorkbenchProduct[]>>(cacheKey, {
        ...cacheData,
        pages: cacheData.pages.map((page) => patchBufferItem(page, id, edits)),
      });
      if (fetchteAktiv) {
        queryClient.invalidateQueries({ queryKey: cacheKey, exact: true, refetchType: 'active' });
      } else if (warVeraltet) {
        queryClient.invalidateQueries({ queryKey: cacheKey, exact: true, refetchType: 'none' });
      }
    }

    markEdited(id);
    return true;
  }, [queryClient, toast, markEdited]);

  const currentDecision = currentProduct ? getReviewDecision(currentProduct.id) : 'pending_review';
  const currentWasEdited = currentProduct ? wasEdited(currentProduct.id) : false;
  const isCurrentReviewed = currentDecision === 'approved' || currentDecision === 'rejected';

  // canGoNext haengt im windowed Pfad bewusst NICHT an `total` (serverTotal
  // kann durch eine Bulk-Invalidierung sinken, siehe oben) — es fragt direkt
  // den Puffer: gibt es ein geladenes naechstes Item, ODER ist noch nicht
  // erschoepft (dann laedt der naechste Schritt einfach nach)? Erst wenn der
  // Puffer wirklich erschoepft ist UND das letzte Item erreicht ist, sperrt
  // "Weiter" (F5).
  // K6-Befund: nach einem Folgeseiten-Fehler stoppt der Vorlader (Nr. 75),
  // aber "Weiter" schob den Zeiger weiter unsichtbar Richtung total — das
  // Nr.-78-Fehlerbild, zurueck ueber den Fehler-Pfad. Der Fehler klemmt wie
  // der Deckel (navClamped, oben bei autoLoadCapReached abgeleitet):
  // Navigation endet an der letzten geladenen Zeile.
  const canGoNext = currentIndex < maxNavigableIndex(total, products.length, navClamped);

  return {
    products,
    isLoading,
    error,
    /**
     * H6-F4: true, wenn die Produktabfrage laufen will, aber nicht kann
     * (React Query pausiert offline statt zu fehlern). Der Reviewer rendert
     * dann den Fehlerzustand — der Leerzustand bleibt der erfolgreichen
     * leeren Antwort vorbehalten.
     */
    isPaused,
    refetch,
    currentProduct,
    currentIndex: displayIndex,
    total,
    sessionAccepted,
    sessionRejected,
    /**
     * Offene (noch nicht freigegebene/abgelehnte) Zeilen der aktuell geladenen
     * Menge. Traegt zusammen mit `mayHaveMore` den "alles geprueft"-Leerzustand
     * — NICHT sessionAccepted/sessionRejected, siehe countUndecidedLoaded.
     */
    undecidedLoadedCount,
    /** Kann es weitere, noch nicht geladene Zeilen geben? Puffer- und paginierter Pfad haben verschiedene Quellen. */
    mayHaveMore,
    currentDecision,
    currentWasEdited,
    isCurrentReviewed,
    getReviewDecision,
    wasEdited,
    acceptProduct,
    rejectProduct,
    /** Toast-Rueckweg einer Ablehnung. Exportiert fuer den K1-Pin. */
    undoReject,
    /** Zurueck in die Pruefung, aus "Freigegeben" wie aus "Abgelehnt". */
    reopenProduct,
    bulkApprove,
    bulkReject,
    /**
     * Sammelfreigabe ueber eine serverseitig bestimmte Menge (Etappe 4).
     * Liefert die geschriebene Anzahl UND die geschriebenen IDs - letztere
     * sind die Voraussetzung dafuer, dass der Aufrufer "Rueckgaengig" anbieten
     * kann.
     */
    approveScope,
    /** Rueckweg dazu: setzt die genannten Zeilen wieder auf `pending_review`. */
    reopenBulk,
    saveChanges,
    goToPrev,
    goToNext,
    goToProduct,
    canGoPrev: currentIndex > 0,
    canGoNext,
    /**
     * Die fuenf feed-weiten Zahlen der Sammelfreigabe (Etappe 4), suchbezogen.
     * `null` heisst "noch nicht geladen" oder "nicht beantwortet" (fremder
     * Feed), NICHT "null Produkte" - die Oberflaeche zeigt dann gar keine Zahl
     * statt einer falschen.
     */
    bulkSummary,
    // --- List-view pagination (P0-4, Task 6): WorkbenchListView is a pure
    // props component and has no direct access to the infinite query, so it
    // needs these threaded through to load further pages and to render the
    // honest "X von Y geladen" counter.
    //
    // N2: gescoped ueber scopeHasNextPageForWindowedPath (siehe deren
    // Doc-Kommentar) — sonst leckt hasNextPage aus dem seit F4 geteilten
    // Infinite-Query-Cache in den windowed Kartenmodus-Standard.
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    /** Raw (pre-scoreFilter) count of rows loaded so far via the infinite query. */
    listLoadedCount,
    /** Feed-wide pending-review count (search-scoped, not scoreFilter-scoped) for the honest counter. Null while unknown. */
    listPendingTotal: serverTotalFailed ? null : (serverTotal ?? null),
    /** K5: true = die Gesamtzahl ist gerade nicht ermittelbar (Zaehl-Query rot). */
    serverTotalUnknown: serverTotalFailed,
    /**
     * K5-Lehre: true = die Zahlen sind gerade nicht ermittelbar. Muss von
     * "keine offenen Produkte" unterscheidbar bleiben, sonst verschwindet die
     * Sammelfreigabe kommentarlos.
     */
    bulkSummaryFailed,
    refetchBulkSummary,
    /** P0-4 Review-Runde 2, Fund N1: true, wenn das automatische Nachladen am Deckel (AUTO_LOAD_ROW_CAP) haengt und der Server noch mehr haette. */
    autoLoadCapReached,
    /** Gibt ein weiteres AUTO_LOAD_ROW_CAP-Batch fuers automatische Nachladen frei (N1-Button "Weiter suchen"). */
    unlockMoreAutoLoad,
  };
}
